"use client";
import ReactDOM from "react-dom";
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  useParams,
  useNavigate,
  useLocation,
  useOutletContext,
} from "react-router-dom";
import { useFuncion } from "../../context/FuncionContext";
import cacheManager from "../../utils/cacheManager";
import { invalidateByPrefix } from "../../services/cacheService";
import {
  ArrowLeft,
  Target,
  CheckCircle,
  ChevronRight,
  Wand2,
  Coins as CoinsIcon,
  HelpCircle,
  Gamepad2,
  AlertTriangle,
} from "lucide-react";
import PreguntaRenderer from "./renderers/PreguntaRenderer";
import LoadingSpinner from "../../components/LoadingSpinner";
import JuegoRenderer from "./renderers/JuegoRenderer";
import ConfirmExitModal from "../../components/ConfirmExitModal";

function TutorialOverlay({
  step,
  currentStep,
  totalSteps,
  onNext,
  onPrev,
  onClose,
  onSkip
}) {
  // Bloqueo de scroll cuando está abierto
  useEffect(() => {
    const prevOverflow = document.body.style.overflow;
    const prevPaddingRight = document.body.style.paddingRight;
    const scrollbarW = window.innerWidth - document.documentElement.clientWidth;

    document.body.style.overflow = "hidden";
    if (scrollbarW > 0) document.body.style.paddingRight = `${scrollbarW}px`;

    const prevent = (e) => e.preventDefault();
    window.addEventListener("wheel", prevent, { passive: false });
    window.addEventListener("touchmove", prevent, { passive: false });

    return () => {
      document.body.style.overflow = prevOverflow;
      document.body.style.paddingRight = prevPaddingRight;
      window.removeEventListener("wheel", prevent);
      window.removeEventListener("touchmove", prevent);
    };
  }, []);

  const isFirst = currentStep === 1;
  const isLast = currentStep === totalSteps;

  const node = (
    <div className="tour-root" role="dialog" aria-modal="true" aria-label="Tutorial">
      {/* Fondo oscurecido y desenfocado */}
      <div className="tour-backdrop" aria-hidden="true" />

      {/* Tarjeta del tutorial (siempre centrada) */}
      <div
        className="tour-card tour-card-centered"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="tour-head">
          <strong className="tour-title">{step?.title || "Tutorial"}</strong>
          <span className="tour-step">
            {currentStep}/{totalSteps}
          </span>
        </div>

        {step?.text ? (
          <div className="tour-text">
            {String(step.text)
              .split(/\n\n/g)
              .filter(Boolean)
              .map((p, i) => {
                const trimmed = p.trim();

                // Si empieza con **, es un título
                if (trimmed.startsWith('**') && trimmed.includes('**', 2)) {
                  const titleMatch = trimmed.match(/\*\*(.*?)\*\*/);
                  const title = titleMatch ? titleMatch[1] : trimmed.replace(/\*\*/g, '');
                  const rest = trimmed.replace(/\*\*.*?\*\*/, '').trim();

                  return (
                    <div key={i} className="tour-section">
                      <h4 className="tour-section-title">{title}</h4>
                      {rest && <p className="tour-p">{rest}</p>}
                    </div>
                  );
                }

                // Si empieza con •, es una viñeta
                if (trimmed.startsWith('•')) {
                  const parts = trimmed.split('**');
                  return (
                    <p key={i} className="tour-p tour-bullet">
                      {parts.map((part, idx) =>
                        idx % 2 === 1 ? <strong key={idx}>{part}</strong> : part
                      )}
                    </p>
                  );
                }

                // Párrafo normal con soporte para negritas
                const parts = trimmed.split('**');
                return (
                  <p key={i} className="tour-p">
                    {parts.map((part, idx) =>
                      idx % 2 === 1 ? <strong key={idx}>{part}</strong> : part
                    )}
                  </p>
                );
              })}
          </div>
        ) : null}

        <div className="tour-actions">
          {!isFirst && (
            <button className="tour-btn ghost" onClick={onPrev} type="button">
              Volver
            </button>
          )}

          <button className="tour-btn primary" onClick={isLast ? onClose : onNext} type="button">
            {isLast ? "¡Comenzar!" : "Siguiente"}
          </button>
        </div>
      </div>
    </div>
  );

  return ReactDOM.createPortal(node, document.body);
}

/* ===================== Overlay de recompensa con CTA ===================== */
function RewardOverlay({ coins = 0, onBack, onNext, hasNext = false, onClose }) {
  const [particles, setParticles] = React.useState([]);

  React.useEffect(() => {
    const parts = Array.from({ length: 20 }, (_, i) => ({
      id: i,
      x: Math.random() * 120 - 60,
      y: Math.random() * -140 - 60,
      delay: Math.random() * 0.3,
      duration: 0.8 + Math.random() * 0.4,
    }));
    setParticles(parts);
  }, []);

  return (
    <div className="reward-overlay" role="dialog" aria-modal="true" aria-label="Recompensa de la tarea">
      <div className="reward-container">
        <div className="reward-particles" aria-hidden>
          {particles.map((p) => (
            <div
              key={p.id}
              className="reward-particle"
              style={{
                "--x": `${p.x}px`,
                "--y": `${p.y}px`,
                animationDelay: `${p.delay}s`,
                animationDuration: `${p.duration}s`,
              }}
            />
          ))}
        </div>

        <div className="reward-content">
          <div className="reward-coin-wrapper" aria-hidden>
            <svg className="reward-coin" viewBox="0 0 100 100" width="120" height="120">
              <defs>
                <linearGradient id="coinGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor="#ffd700" />
                  <stop offset="50%" stopColor="#ffed4e" />
                  <stop offset="100%" stopColor="#ffa500" />
                </linearGradient>
                <filter id="coinGlow">
                  <feGaussianBlur stdDeviation="4" result="coloredBlur" />
                  <feMerge>
                    <feMergeNode in="coloredBlur" />
                    <feMergeNode in="SourceGraphic" />
                  </feMerge>
                </filter>
              </defs>
              <circle cx="50" cy="50" r="45" fill="url(#coinGrad)" filter="url(#coinGlow)" />
              <circle cx="50" cy="50" r="38" fill="none" stroke="#ffa500" strokeWidth="2" opacity="0.6" />
              <text x="50" y="50" textAnchor="middle" dominantBaseline="central" fontSize="32" fontWeight="bold" fill="#8b4513">
                ₡
              </text>
            </svg>
            <div className="reward-coin-shine" />
          </div>

          <h2 className="reward-title">¡Tarea Completada!</h2>

          <div className="reward-amount">
            <span className="reward-plus">+</span>
            <span className="reward-value">{coins}</span>
            <span className="reward-label">UTMCoins</span>
          </div>

          <p className="reward-message">
            {coins > 0 ? "¡Sigue así, lo estás haciendo genial! 🎉" : "Ya habías reclamado las monedas de esta tarea."}
          </p>

          <div className="rt-buttons" style={{ marginTop: 16 }}>
            <button onClick={onBack} className="rt-btn rt-btn-light">
              Volver al Componente
            </button>
            {hasNext && (
              <button onClick={onNext} className="rt-btn rt-btn-primary rt-btn-inline">
                Siguiente tarea <ChevronRight size={16} />
              </button>
            )}
          </div>

          {onClose && (
            <button onClick={onClose} aria-label="Cerrar" className="rt-btn rt-btn-ghost reward-close-btn">
              ×
            </button>
          )}
        </div>

        <div className="reward-rings" aria-hidden>
          <div className="reward-ring" style={{ animationDelay: "0s" }} />
          <div className="reward-ring" style={{ animationDelay: "0.3s" }} />
          <div className="reward-ring" style={{ animationDelay: "0.6s" }} />
        </div>
      </div>
    </div>
  );
}


/* ===================== Modal bonito de compra de Comodín ===================== */
function BuyComodinModal({
  open,
  item,
  coinsNow = 0,
  onCancel,
  onConfirm,
  confirming = false,
  canBuy = false,
}) {
  if (!open || !item) return null;

  const price = Number(item.price || 0);
  const after = Math.max(0, coinsNow - price);
  const missing = Math.max(0, price - coinsNow);

  const name = item.nombre || "Comodín";

  return (
    <div
      className="buy-overlay"
      role="dialog"
      aria-modal="true"
      aria-label="Comprar comodín"
    >
      <div className="buy-card">
        {/* Cinta superior */}
        <div className="buy-ribbon">
          <span className="buy-ribbon-pill">Compra de comodín</span>
        </div>

        {/* Botón cerrar */}
        <button
          type="button"
          className="buy-close"
          onClick={onCancel}
          aria-label="Cerrar"
        >
          ×
        </button>

        {/* Icono + título */}
        <div className="buy-header">
          <div className="buy-icon-wrap" aria-hidden>
            <div className="buy-icon-circle">✨</div>
          </div>

          <div className="buy-header-text">
            <h3 className="buy-title">{name}</h3>
            <p className="buy-sub">
              Usa este comodín para ayudarte a <strong>seguir avanzando</strong> en esta tarea
              cuando las vidas o las preguntas se compliquen.
            </p>
          </div>
        </div>

        {/* Resumen compacto de saldo */}
        <div className="buy-stats">
          <div className="buy-stat">
            <div className="buy-stat-label">Saldo actual</div>
            <div className="buy-stat-value">{coinsNow} UTMCoins</div>
          </div>
          <div className="buy-stat buy-stat-main">
            <div className="buy-stat-label">Precio</div>
            <div className="buy-stat-value">{price} UTMCoins</div>
          </div>
          <div className="buy-stat">
            <div className="buy-stat-label">Saldo tras la compra</div>
            <div className="buy-stat-value">{after} UTMCoins</div>
          </div>
        </div>

        {/* Mensaje si falta saldo */}
        {!canBuy && (
          <div className="buy-warning">
            Te faltan <b>{missing}</b> UTMCoins para completar esta compra.
          </div>
        )}

        {/* Nota breve */}
        <div className="buy-note">
          El comodín se aplica <strong>solo al componente actual</strong> y esta operación
          <strong> no es reembolsable</strong>.
        </div>

        {/* Botones */}
        <div className="buy-actions">
          <button
            type="button"
            className="rt-btn rt-btn-ghost"
            onClick={onCancel}
          >
            Cancelar
          </button>
          <button
            type="button"
            className="rt-btn rt-btn-primary"
            onClick={onConfirm}
            disabled={confirming || !canBuy}
            title={
              canBuy
                ? "Comprar comodín para este componente"
                : "Saldo insuficiente o sin stock"
            }
          >
            {confirming ? "Comprando…" : "Confirmar compra"}
          </button>
        </div>
      </div>
    </div>
  );
}


/* ===================== Modal de respuesta correcta (auto-avance) ===================== */
function CorrectModal({ autoMs = 1800, onContinue }) {
  useEffect(() => {
    const t = setTimeout(() => onContinue?.(), autoMs);
    return () => clearTimeout(t);
  }, [autoMs, onContinue]);

  return (
    <div className="cm-overlay" role="dialog" aria-modal="true" aria-label="Respuesta correcta">
      <div className="cm-card">
        <div className="cm-icon">
          <CheckCircle size={28} />
        </div>
        <h3 className="cm-title">¡Respuesta correcta!</h3>
        <p className="cm-sub">Pasando a la siguiente actividad…</p>

        <div className="cm-progress" style={{ ["--cm-ms"]: `${autoMs}ms` }} />

        <div className="cm-actions">
          <button className="rt-btn rt-btn-success" onClick={onContinue}>
            Continuar ahora
          </button>
        </div>
      </div>
    </div>
  );
}



/* ===================== Subbloques reutilizables ===================== */
function TaskInfoCard({ tarea, isReview }) {
  if (!tarea) return null;
  return (
    <div className="rt-card rt-pop">
      <div className="rt-eyebrow rt-eyebrow-blue">Tarea</div>
      <h2 className="rt-title">{tarea.nombre}{isReview ? " — Revisión" : ""}</h2>
    </div>
  );
}

// Helper: interpretar banderas tipo secuencia_hijos / secuencial_hijos
const flagToBoolean = (value, defaultValue = false) => {
  if (value === undefined || value === null) return !!defaultValue;
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value !== 0;
  if (typeof value === "string") {
    const v = value.trim().toLowerCase();
    if (["true", "1", "yes", "si", "sí", "on", "t"].includes(v)) return true;
    if (["false", "0", "no", "off", "f"].includes(v)) return false;
  }
  return !!defaultValue;
};


// Fisher–Yates in-place (retorna copia barajada)
function shuffleArray(list) {
  const a = Array.isArray(list) ? [...list] : [];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

function ProgressBlock({ total, correctCount, pct, activities, queue, results, isReview, questionCount, gameCount, lives = 3 }) {
  return (
    <div className="rt-card">
      <div className="rt-chips">
        <span className="rt-chip rt-chip-q" title={`${questionCount} pregunta(s)`}>
          <HelpCircle size={14} /> <strong>{questionCount}</strong>
        </span>
        {gameCount > 0 && (
          <span className="rt-chip rt-chip-g" title={`${gameCount} juego(s)`}>
            <Gamepad2 size={14} /> <strong>{gameCount}</strong>
          </span>
        )}

      </div>

      {/* Progreso general */}
      <div className="rt-row rt-row-between rt-row-gap" style={{ marginTop: 6 }}>
        <div className="rt-row rt-row-gap">
          <Target size={16} className="rt-icon-blue" />
          <strong className="rt-strong">{correctCount}</strong>
          <span className="rt-dim">/ {total} {isReview ? "contestadas" : "correctas"}</span>
        </div>
        <div className={`rt-badge ${pct === 100 ? "rt-badge-success" : ""}`}>{pct}%</div>
      </div>

      <div className="rt-progress">
        <div className="rt-progress-bar" style={{ width: `${pct}%` }} />
        <div className="rt-progress-stripes" />
      </div>

      {/* Mapa de puntos por actividad con indicador de actual */}
      <div className="rt-dots">
        {activities.map((a, i) => {
          const r = results[a.id] || { correct: false };
          const isCurrent = !isReview && i === (queue[0] ?? null);
          return (
            <div
              key={a.id}
              title={`${a._label ?? "Actividad"} #${i + 1} - ${r.correct ? "Correcta" : "Pendiente"}`}
              className={`rt-dot ${r.correct ? "rt-dot-ok" : ""} ${isCurrent ? "rt-dot-current" : ""}`}
            />
          );
        })}
      </div>
    </div>
  );
}

function ComodinBlock({
  isReview,
  comodinLoading,
  comodin,
  canBuyComodin,
  buyingComodin,
  setConfirmBuyOpen,
  coinsNow,
  comodinMsg,
}) {
  if (isReview) return null;

  const price = Number(comodin?.price || 0);
  const faltan =
    comodin && !canBuyComodin
      ? Math.max(0, price - Number(coinsNow || 0))
      : 0;

  return (
    <div className="rt-card rt-comodin-card">
      {/* Header */}
      <div className="rt-row rt-row-between rt-comodin-header">
        <div className="rt-row rt-row-gap">
          <div className="rt-comodin-icon">
            <Wand2 size={16} />
          </div>

          <div>
            <div className="rt-comodin-title">Compra de comodines</div>
            <div className="rt-comodin-sub">
              Puedes comprarlos cuando quieras.
            </div>
          </div>
        </div>

        <div className="rt-comodin-status">
          {comodinLoading ? (
            <span className="rt-tag rt-tag-muted">Cargando…</span>
          ) : !comodin ? (
            <span className="rt-tag rt-tag-muted">No disponible</span>
          ) : (
            <div className="rt-tag rt-tag-price" title="Precio del comodín">
              Valor del comodín: <CoinsIcon size={14} />
              <span>{price}</span>
            </div>
          )}
        </div>
      </div>

      {/* Cuerpo */}
      <div className="rt-comodin-body">

        <button
          className={`rt-btn rt-btn-primary rt-comodin-btn ${!canBuyComodin ? "rt-btn-disabled-bw" : ""}`}
          disabled={!comodin || !canBuyComodin || buyingComodin}
          onClick={() => setConfirmBuyOpen(true)}
          title={
            canBuyComodin
              ? "Comprar comodín para este componente"
              : "Saldo insuficiente o sin stock"
          }
        >
          {buyingComodin ? "Comprando…" : "Comprar comodín"}
        </button>

        {comodinMsg?.msg && (
          <div
            className={`rt-banner ${comodinMsg.type === "ok" ? "rt-banner-ok" : "rt-banner-err"
              }`}
          >
            {comodinMsg.msg}
          </div>
        )}
      </div>
    </div>
  );
}


/* ===================== Hook unificado de controles (skip/retry/consumos + vidas) ===================== */
function useTaskControls({ tareaId, componentId, goNext, getComodinesState, callComodin, isFirstAttempt }) {
  const [estado, setEstado] = React.useState({
    comodines: 3,
    sinGanancias: false,
    bloqueadoGlobal: false,
    vidas: 3,
    ctaComodines: false, // 👈 NUEVO: hace brillar el botón comodines
  });

  const [modals, setModals] = React.useState({
    skipBlocked: false,
    noComodines: false,
    sinGanancias: false,

    // intento #1: modal bonificación (el que ya tienes)
    noLives: false,

    // intento #2+ con comodines: modal “vidas agotadas” + botón “Usar comodín”
    noLivesUseComodin: false,

    // sin vidas y sin comodines: modal “solo salir”
    noLivesNoComodines: false,
  });



  const refresh = React.useCallback(async () => {
    if (!getComodinesState) return;
    if (!componentId && !tareaId) return;

    // 👈 Pedimos estado con ambos IDs: vidas por tarea, comodines por componente
    const data = await getComodinesState({
      componenteId: componentId ?? undefined,
      tareaId: tareaId ?? undefined,
    }).catch(() => null);

    setEstado((s) => ({
      ...s,
      comodines: Number.isFinite(data?.comodines_restantes) ? Number(data.comodines_restantes) : s.comodines,
      vidas: Number.isFinite(data?.vidas_restantes) ? Number(data.vidas_restantes) : s.vidas,
      sinGanancias: !!data?.sin_ganancias,
      bloqueadoGlobal: !!data?.bloqueado_global,
    }));
  }, [componentId, tareaId, getComodinesState]);


  React.useEffect(() => { refresh(); }, [refresh]);

  const applyServerState = (data, fallback = {}) => {
    setEstado((s) => {
      const comodines = Number.isFinite(data?.comodines_restantes) ? data.comodines_restantes : (fallback.decComodin ? Math.max(0, s.comodines - 1) : s.comodines);
      const vidas = Number.isFinite(data?.vidas_restantes) ? data.vidas_restantes : (fallback.decVida ? Math.max(0, s.vidas - 1) : s.vidas);
      const sinG = !!data?.sin_ganancias || comodines <= 0;
      const bloqueadoG = !!data?.bloqueado_global && !sinG ? data.bloqueado_global : s.bloqueadoGlobal;
      return { ...s, comodines, vidas, sinGanancias: sinG, bloqueadoGlobal: bloqueadoG };
    });
  };

  // Consumo genérico de comodín (pista/reintentar/otros)
  const consume = async (kind) => {
    const id = String(componentId ?? tareaId ?? "");
    if (!id || !callComodin) return { ok: false };
    if (estado.comodines <= 0) {
      setModals(m => ({ ...m, noComodines: true }));
      return { ok: false, reason: "no-comodines" };
    }
    try {
      const data = await callComodin(id, {
        kind,
        componenteId: componentId ?? undefined,
        tareaId: componentId ? undefined : tareaId,
      });
      applyServerState(data, { decComodin: 1 });
      setEstado((s) => ({ ...s, ctaComodines: false }));
      return { ok: true, ...data };
    } catch (e) {
      return { ok: false, error: e };
    }
  };

  // Reintentar (usa comodín)
  const retry = async () => consume("reintentar");

  // Saltar
  const skip = ({ locked } = {}) => {
    if (locked && !estado.sinGanancias && !estado.bloqueadoGlobal && estado.comodines > 0) {
      setModals(m => ({ ...m, skipBlocked: true }));
      return;
    }
    goNext();
  };
  const confirmSkipBlocked = () => {
    setModals(m => ({ ...m, skipBlocked: false }));
    setEstado(s => ({ ...s, bloqueadoGlobal: true }));
    goNext();
  };

  const loseLife = async () => {
    // helper: abre SOLO un modal de vidas (cierra los demás)
    const openLivesModal = (which) => {
      setModals((m) => ({
        ...m,
        // cerrar todos los modales de vidas antes de abrir uno
        noLives: false,
        noLivesUseComodin: false,
        noLivesNoComodines: false,
        [which]: true,
      }));
    };

    // Si ya estás en 0, no sigas restando: decide modal según intento y comodines
    if (estado.vidas <= 0) {
      const hasComodines = (estado.comodines ?? 0) > 0;

      if (isFirstAttempt) {
        openLivesModal("noLives"); // intento #1 => bonificación (tu modal)
      } else {
        openLivesModal(hasComodines ? "noLivesUseComodin" : "noLivesNoComodines");
      }
      return;
    }

    let afterLives = Math.max(0, estado.vidas - 1);
    let srvComodines = estado.comodines;
    let srvVidas = afterLives;

    const ownerCompId = componentId ?? null;
    const idForCall = String(ownerCompId ?? tareaId ?? "");

    try {
      const data = await callComodin?.(idForCall, {
        kind: "quitar_vida",
        componenteId: ownerCompId ?? undefined,
        tareaId,
      });

      srvVidas = Number.isFinite(data?.vidas_restantes) ? Number(data.vidas_restantes) : afterLives;
      srvComodines = Number.isFinite(data?.comodines_restantes) ? Number(data.comodines_restantes) : estado.comodines;

      applyServerState(data, { decVida: !Number.isFinite(data?.vidas_restantes) ? 1 : 0 });
    } catch {
      setEstado((s) => ({ ...s, vidas: afterLives }));
      srvVidas = afterLives;
      srvComodines = estado.comodines;
    }

    // Si te quedaste sin vidas: abrir SOLO el modal correcto (y cerrar los demás)
    if (srvVidas <= 0) {
      if (isFirstAttempt) {
        openLivesModal("noLives");
      } else {
        openLivesModal(srvComodines > 0 ? "noLivesUseComodin" : "noLivesNoComodines");
      }
    }
  };


  const setCtaComodines = (flag) => {
    setEstado((s) => ({ ...s, ctaComodines: !!flag }));
  };


  return {
    estado,              // { comodines, sinGanancias, bloqueadoGlobal, vidas }
    consume,
    retry,
    skip,
    loseLife,            // 👈 NUEVO
    modals, setModals,
    confirmSkipBlocked,
    refresh,
    setCtaComodines,
  };
}



/* ============================= Componente principal ============================= */
export default function ResolverTarea() {
  const { codigoCurso, tareaId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { setFooterVisible } = useOutletContext() || {};
  const [hardLockNoLives, setHardLockNoLives] = useState(false);
  const [openComodinesSignal, setOpenComodinesSignal] = useState(0);

  const {
    resolveStudentCourseByCode,
    getCourseStructureStudent,
    isElementCompleted,
    reportTaskCompletion,
    getTaskSubmission,
    utmCoins,
    fetchStoreItems,
    buyStoreItem,
    refreshUTMCoins,
    getStudentStreak,
    getComodinesState,
    useComodin: callComodin,
    startTaskAttempt
  } = useFuncion();

  const [loading, setLoading] = useState(true);
  const [curso, setCurso] = useState(null);
  const [tarea, setTarea] = useState(null);
  const [attemptNo, setAttemptNo] = useState(null);
  const [baseReward, setBaseReward] = useState(0);
  const isFirstTaskAttempt = Number(attemptNo) === 1;

  // ✅ 1. Estados del tutorial
  const [tourOpen, setTourOpen] = useState(false);
  const [tourStep, setTourStep] = useState(0);

  // ✅ 2. Helper para obtener ID único (DEBE IR ANTES de tutorialKey)
  const getUserIdFromToken = useCallback(() => {
    try {
      const token =
        localStorage.getItem("token") ||
        localStorage.getItem("accessToken") ||
        sessionStorage.getItem("token") ||
        sessionStorage.getItem("accessToken");

      if (!token) {
        console.warn("⚠️ No se encontró ningún token");
        return null;
      }

      const payload = JSON.parse(atob(token.split(".")[1]));

      // ✅ CAMBIO CRÍTICO: Agregar usuario_id como primera opción
      const userId =
        payload?.usuario_id ||      // ⬅️ NUEVO: Tu backend usa este campo
        payload?.id ||
        payload?.user_id ||
        payload?.sub ||
        payload?.codigo_institucional ||
        null;

      if (!userId) {
        console.warn("⚠️ No se encontró ID en el payload:", payload);
        return null;
      }


      return userId;
    } catch (err) {
      console.warn("Error extrayendo userId del token:", err);
      return null;
    }
  }, []);

  // ✅ 3. Key único por usuario y tarea (USA getUserKeyFromStorage)
  const tutorialKey = useMemo(() => {
    const userId = getUserIdFromToken();
    if (!userId) return null;
    return `resolver_tutorial_seen:student_${userId}`;
  }, [getUserIdFromToken]);

  // ✅ 4. Pasos del tutorial (con formato mejorado)
  const TOUR_STEPS = useMemo(
    () => [
      {
        key: "task_sidebar",
        title: "PANEL DE INFORMACIÓN",
        text:
          "Aquí verás toda la información importante de la tarea: tipo de actividad, respuestas acertadas y la opción de comprar comodines.",
      },
      {
        key: "main_activity",
        title: "ZONA DE ACTIVIDADES",
        text:
          "**Sistema de Vidas **\n\n" +
          "Tienes 3 vidas para cada tarea. Al fallar una pregunta, pierdes una vida.\n\n" +
          "**Comodines disponibles **\n\n" +
          "• **Pista:** Te ayuda con la pregunta actual\n\n" +
          "• **Vida extra:** Recupera una vida perdida\n\n" +
          "• **¡OJO!** Podrás usarlos cuando ya no tengas vidas disponibles\n\n" +
          "¡Ahora puedes comenzar a responder!",
      },
    ],
    []
  );
  // toast de correcta + temporizador para auto avance
  const [showCorrectToast, setShowCorrectToast] = useState(false);
  const correctToastTimerRef = useRef(null);
  const AUTO_ADVANCE_MS = 2000;

  // avanzar después del toast
  const advanceAfterCorrect = useCallback(() => {
    setQueue((prevQueue) => {
      if (!prevQueue.length) return prevQueue;
      const [, ...rest] = prevQueue;
      return rest;
    });
  }, []);

  // componente dueño
  const [componentId, setComponentId] = useState(location.state?.componentId ?? null);
  const [nextTask, setNextTask] = useState(null);

  // ==== Quick-buy de comodín ====
  const [comodin, setComodin] = useState(null);
  const [comodinLoading, setComodinLoading] = useState(false);
  const [buyingComodin, setBuyingComodin] = useState(false);
  const [comodinMsg, setComodinMsg] = useState({ type: "", msg: "" });
  const [comodinesRefreshKey, setComodinesRefreshKey] = useState(0);
  const [confirmBuyOpen, setConfirmBuyOpen] = useState(false);

  const ensureLivesOnEnter = useCallback(
    async (compId) => {
      if (!compId && !tareaId) return;
      try {
        // 👇 Pedimos STATE con ambos IDs para leer vidas correctas de la tarea
        const st = await getComodinesState?.({
          componenteId: compId ?? undefined,
          tareaId: Number(tareaId),
        }).catch(() => null);

        const vidas = Number(st?.vidas_restantes ?? NaN);

        // Si el backend reporta 0 vidas, reseteamos vidas de la TAREA
        if (Number.isFinite(vidas) && vidas <= 0 && compId) {
          await callComodin?.(String(compId), {
            kind: "reset_vidas",
            componenteId: compId,
            tareaId: Number(tareaId),
          });
        }
      } catch { /* no-op */ }
    },
    [getComodinesState, callComodin, tareaId]
  );


  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        setComodinLoading(true);
        const res = await fetchStoreItems?.({ onlyActive: true }).catch(() => ({}));
        const raw = Array.isArray(res?.items) ? res.items : res?.products || [];
        const first = raw.find((it) => String(it?.categoria ?? it?.tipo ?? "").toLowerCase() === "comodin");
        if (alive && first) {
          setComodin({
            id: first.id,
            nombre: first.nombre || first.name || "Comodín",
            price: Number(first.precio_coins ?? first.price_coins ?? first.price ?? 0),
            activo: (first.activo ?? first.active ?? true) === true,
            stock: first.stock_infinito === true ? null : first.stock_actual ?? first.stock ?? null,
            limit_per_user: first.limit_per_user ?? first.limit ?? null,
          });
        }
      } finally {
        alive && setComodinLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [fetchStoreItems]);



  const coinsNow = Number(utmCoins?.cantidad_actual ?? 0);
  const canBuyComodin =
    !!comodin &&
    comodin.activo &&
    (comodin.stock == null || Number(comodin.stock) > 0) &&
    coinsNow >= Number(comodin.price || 0);

  const buyOneComodin = useCallback(async () => {
    if (!comodin || !buyStoreItem) return;
    try {
      setBuyingComodin(true);
      setComodinMsg({ type: "", msg: "" });
      const res = await buyStoreItem({
        itemId: comodin.id,
        quantity: 1,
        componentId: componentId ?? null,
      });
      const ok = Boolean(res?.ok ?? res?.success ?? res?.purchase?.id ?? res?.data?.purchase?.id ?? res?.id);
      if (ok) {
        setComodinMsg({ type: "ok", msg: "Comodín comprado y aplicado al componente." });

        // 1) refresca coins globales (UI de monedas)
        // 2) refresca el estado real de comodines/vidas del servidor (UI de la tarea)
        await Promise.all([
          refreshUTMCoins?.(),
          controls?.refresh?.(), // ✅ ESTO ES LO QUE TE FALTABA
        ]);

        // Mantén tu key si la usas para forzar re-render en renderers
        setComodinesRefreshKey((k) => k + 1);
      } else {
        throw new Error(res?.message || "No se pudo completar la compra.");
      }

    } catch (e) {
      setComodinMsg({ type: "err", msg: e?.message || "Error al comprar." });
    } finally {
      setBuyingComodin(false);
    }
  }, [comodin, buyStoreItem, refreshUTMCoins, componentId]);

  // actividades de la tarea
  const [activities, setActivities] = useState([]);
  const [queue, setQueue] = useState([]);
  const [results, setResults] = useState({});

  // control envío / completado
  const hasLoadedRef = useRef(false);
  const sentCompletionRef = useRef(false);
  const [completionSent, setCompletionSent] = useState(false);
  const [startedAt] = useState(() => new Date());
  const [sending, setSending] = useState(false);

  // recompensa
  const [showReward, setShowReward] = useState(false);
  const [coinsEarned, setCoinsEarned] = useState(0);

  // confirmar salida
  const [confirmExitOpen, setConfirmExitOpen] = useState(false);

  // listeners popstate
  const popSetupRef = useRef(false);

  // marca: usuario realmente ya aceptó irse
  const isTryingHardLeaveRef = useRef(false);

  // modo navegación
  const navMode = location.state?.mode; // "start" | "review"
  const isReview = navMode === "review";
  // ✅ Si vienes desde ComponenteTareas, NO es acceso directo (evita bloqueo injusto)
  const cameFromComponent =
    !!location.state?.componentId ||
    !!location.state?.tarea ||
    Array.isArray(location.state?.actividades);

  // bandera: la tarea YA estaba completa antes de entrar
  const wasCompletedBefore = location.state?.alreadyCompleted ?? false;

  // ✅ useEffect separado para manejar el tutorial
  useEffect(() => {
    // Guard 1: No mostrar mientras carga
    if (loading) return;

    // Guard 2: No mostrar en modo revisión
    if (isReview || wasCompletedBefore) return;

    // Guard 3: Esperar que haya actividades cargadas
    if (!activities.length) return;

    // Guard 4: Si no hay tutorialKey, no guardar (pero sí mostrar)
    if (!tutorialKey) {
      console.warn("No se pudo obtener el ID del usuario para guardar el tutorial");
      // Aún así, mostrar el tutorial una vez en esta sesión
      const timer = setTimeout(() => {
        setTourStep(0);
        setTourOpen(true);
      }, 500);
      return () => clearTimeout(timer);
    }

    // Guard 5: Verificar si ya vio el tutorial
    try {
      const seen = localStorage.getItem(tutorialKey);
      if (seen === "1") {
        // Ya vio el tutorial, no mostrar

        return;
      }
    } catch (err) {
      console.warn("Error verificando tutorial en localStorage:", err);
    }

    // Mostrar tutorial después de un delay para asegurar que el DOM esté listo

    const timer = setTimeout(() => {
      setTourStep(0);
      setTourOpen(true);
    }, 500);

    return () => clearTimeout(timer);
  }, [loading, activities.length, isReview, wasCompletedBefore, tutorialKey]);

  // ✅ Funciones para controlar el tutorial
  const nextTourStep = useCallback(() => {
    if (tourStep < TOUR_STEPS.length - 1) {
      setTourStep(prev => prev + 1);
    } else {
      closeTour();
    }
  }, [tourStep, TOUR_STEPS.length]);

  const prevTourStep = useCallback(() => {
    if (tourStep > 0) {
      setTourStep(prev => prev - 1);
    }
  }, [tourStep]);

  const closeTour = useCallback(() => {
    setTourOpen(false);
    setTourStep(0);

    if (tutorialKey) {
      try {
        localStorage.setItem(tutorialKey, "1");
      } catch (err) {
        console.warn("Error guardando estado del tutorial:", err);
      }
    }
  }, [tutorialKey]);

  const skipTour = useCallback(() => {
    closeTour();
  }, [closeTour]);

  /* ========= helpers ========= */
  const toStr = (v) => {
    if (v == null) return "";
    if (typeof v === "string") return v;
    try {
      return JSON.stringify(v);
    } catch {
      return String(v);
    }
  };
  const norm = (s) => toStr(s).normalize("NFD").replace(/\p{Diacritic}/gu, "").trim().toLowerCase();
  const toArray = (v) => (Array.isArray(v) ? v : v == null || v === "" ? [] : [v]);
  const isPlainObject = (x) => x != null && typeof x === "object" && !Array.isArray(x);

  const normalizeAnswerByType = (tipo, raw) => {
    const t = (tipo || "").toLowerCase();
    switch (t) {
      case "opcion_multiple":
        if (raw == null || raw === "") return null;
        return Number.isNaN(Number(raw)) ? String(raw) : Number(raw);
      case "seleccion_multiple":
      case "multi_seleccion": {
        const arr = Array.isArray(raw) ? raw : raw == null ? [] : [raw];
        const normArr = [...new Set(arr.map((v) => (Number.isNaN(Number(v)) ? String(v) : Number(v))))];
        normArr.sort((a, b) => (a > b ? 1 : a < b ? -1 : 0));
        return normArr;
      }
      case "verdadero_falso":
        return typeof raw === "boolean" ? raw : String(raw).toLowerCase() === "true";
      case "completar":
      case "respuesta_corta":
        return (raw ?? "").toString().trim();
      default:
        return raw ?? null;
    }
  };

  const compareByType = (tipo_pregunta, expected, respuesta) => {
    const t = (tipo_pregunta || "").toLowerCase();

    if (t === "seleccion_multiple" || t === "multi_seleccion") {
      const exp = Array.isArray(expected?.correctas) ? expected.correctas : Array.isArray(expected) ? expected : [];
      const res = Array.isArray(respuesta) ? respuesta : respuesta == null ? [] : [respuesta];
      const toKey = (v) => (Number.isNaN(Number(v)) ? String(v) : Number(v));
      const A = [...new Set(exp.map(toKey))].sort();
      const B = [...new Set(res.map(toKey))].sort();
      if (A.length === 0) return null;
      if (A.length !== B.length) return false;
      for (let i = 0; i < A.length; i++) if (A[i] !== B[i]) return false;
      return true;
    }

    if (t === "opcion_multiple") {
      const exp = expected?.correcta ?? expected;
      if (exp == null || exp === "") return null;
      const left = Number.isNaN(Number(respuesta)) ? String(respuesta) : Number(respuesta);
      const right = Number.isNaN(Number(exp)) ? String(exp) : Number(exp);
      return left === right;
    }

    if (t === "verdadero_falso") {
      const exp = expected?.correcta ?? expected;
      if (typeof exp === "undefined" || exp === null) return null;
      return Boolean(respuesta) === Boolean(exp);
    }

    if (t === "completar") {
      if (isPlainObject(expected) && isPlainObject(respuesta)) {
        const keys = Object.keys(expected);
        if (keys.length === 0) return null;
        return keys.every((k) => norm(respuesta[k]) === norm(expected[k]));
      }
      const expArr = toArray(expected).map(norm).sort();
      const ansArr = toArray(respuesta).map(norm).sort();
      if (expArr.length === 0) return null;
      if (expArr.length !== ansArr.length) return false;
      return expArr.every((v, i) => v === ansArr[i]);
    }

    if (t === "respuesta_corta") {
      const opts = toArray(expected).map(norm).filter(Boolean);
      if (opts.length === 0) return null;
      return opts.includes(norm(respuesta));
    }

    if (expected == null || expected === "") return null;
    return norm(respuesta) === norm(expected);
  };

  const buildSubmissionPayload = (cursoId, tareaIdNum, acts, resMap) => {
    const qActs = acts.filter((a) => a.tipo_actividad === "pregunta");
    const answers = qActs.map((a) => {
      const r = resMap[a.id] || {};
      const tipo = r?.meta?.tipo_pregunta || a?.tipo_pregunta || a?.tipo || "pregunta";
      const rawAnswer = r?.answer ?? null;
      const respuesta = normalizeAnswerByType(tipo, rawAnswer);
      const rawId = a.estructura_pregunta_id ?? a.id;
      const actividad_id =
        typeof rawId === "number"
          ? rawId
          : Number.isFinite(Number(rawId))
            ? Number(rawId)
            : String(rawId);

      return {
        actividad_id,
        tipo_pregunta: tipo,
        respuesta,
        puntos: r?.meta?.puntos ?? a?.puntos ?? 1,
        explicacion: r?.meta?.explicacion ?? a?.explicacion ?? null,
      };
    });

    if (answers.length) {
      return { curso_id: Number(cursoId), tarea_id: Number(tareaIdNum), answers };
    }
    const gActs = acts.filter((a) => a.tipo_actividad === "juego");
    const actividades = gActs.map((a) => {
      const r = resMap[a.id] || {};
      return { id: a.id, tipo: "juego", correct: !!r.correct, attempts: 1 };
    });
    return { curso_id: Number(cursoId), tarea_id: Number(tareaIdNum), actividades };
  };

  const collectTaskActivities = useCallback((tareaObj) => {
    if (!tareaObj) return [];
    const childActs = Array.isArray(tareaObj.children) ? tareaObj.children.filter((ch) => ch?.tipo === "actividad") : [];
    if (childActs.length > 0) {
      return childActs.map((a) => ({
        ...a,
        tipo: "actividad",
        tipo_actividad: a.tipo_actividad ?? (a.tipo === "juego" ? "juego" : "pregunta"),
        _label: a.tipo_actividad === "juego" ? "Juego" : "Pregunta",
      }));
    }

    const list = [];
    if (Array.isArray(tareaObj.preguntas)) {
      tareaObj.preguntas.forEach((p, idx) => {
        list.push({
          ...p,
          id: p.id ?? `pregunta-${tareaObj.id}-${idx}`,
          tipo: "actividad",
          tipo_actividad: "pregunta",
          parent_id: tareaObj.id,
          _label: "Pregunta",
        });
      });
    }
    if (Array.isArray(tareaObj.juegos)) {
      tareaObj.juegos.forEach((g, idx) => {
        list.push({
          ...g,
          id: g.id ?? `juego-${tareaObj.id}-${idx}`,
          tipo: "actividad",
          tipo_actividad: "juego",
          parent_id: tareaObj.id,
          _label: "Juego",
        });
      });
    }
    return list;
  }, []);

  const findComponentIdForTask = useCallback((struct, tid) => {
    for (const n of struct) {
      for (const m of n.children || []) {
        for (const comp of m.children || []) {
          if ((comp.children || []).some((t) => String(t.id) === String(tid))) {
            return comp.id;
          }
        }
      }
    }
    return null;
  }, []);

  /* ============================== derivados ============================== */
  const total = activities.length;
  const correctCount = useMemo(() => Object.values(results).filter((r) => r.correct).length, [results]);
  const done = total > 0 && correctCount === total;

  const questionCount = useMemo(() => activities.filter((a) => a.tipo_actividad === "pregunta").length, [activities]);
  const gameCount = useMemo(() => activities.filter((a) => a.tipo_actividad === "juego").length, [activities]);
  const pct = total ? Math.round((correctCount / total) * 100) : 0;

  const goNext = useCallback(() => {
    setQueue((prev) => {
      if (prev.length === 0) return prev;
      const [head, ...rest] = prev;
      return [...rest, head];
    });
  }, []);
  const skipQuestion = goNext;
  // === Controles unificados para Saltar/Reintentar/Consumos de comodines ===
  const controls = useTaskControls({
    tareaId: tarea?.id,
    componentId,
    goNext,
    getComodinesState,
    callComodin,
    onOpenBuyComodin: () => setConfirmBuyOpen(true),
    isFirstAttempt: isFirstTaskAttempt, // ✅ NUEVO
  });

  /* ====================== envío/completado ====================== */
  const sendTaskCompletion = useCallback(async () => {
    if (!curso || !tarea || !reportTaskCompletion) return null;

    const finishedAt = new Date();
    const answersPayload = buildSubmissionPayload(curso.id, tarea.id, activities, results);

    const metaWrap = {
      ...answersPayload,
      started_at: startedAt.toISOString(),
      finished_at: finishedAt.toISOString(),
      component_id: componentId ?? null,
      meta: {
        from: "ResolverTarea.jsx",
        user_agent: navigator?.userAgent ?? "",
        attempt_no: Number.isFinite(Number(attemptNo)) ? Number(attemptNo) : null,
        comodines_restantes: Number.isFinite(Number(controls?.estado?.comodines)) ? Number(controls.estado.comodines) : null,
      },
    };

    const r = await reportTaskCompletion(metaWrap);
    const userScope = cacheManager.safeKey(getUserIdFromToken() || "anon");

    cacheManager.invalidateCourse(curso.id, userScope);
    cacheManager.invalidateCourseMeta(codigoCurso, userScope);
    invalidateByPrefix(`examBookings:${userScope}:/api/courses/${curso.id}/structure`);
    invalidateByPrefix(`examBookings:${userScope}:/api/courses/student?`);

    await Promise.allSettled([
      getCourseStructureStudent?.(curso.id),
      refreshUTMCoins?.(),
      getStudentStreak?.(),
    ]);

    try {
      window.dispatchEvent(
        new CustomEvent("student-course-progress-updated", {
          detail: {
            courseId: curso.id,
            codigoCurso,
            userScope,
            completedTaskId: tarea.id,
            completedComponentId: componentId ?? null,
            source: "ResolverTarea",
          },
        }),
      );
    } catch {}

    sentCompletionRef.current = true;
    setCompletionSent(true);
    return r;
  }, [curso, tarea, activities, results, reportTaskCompletion, startedAt, componentId, attemptNo, controls, getUserIdFromToken, codigoCurso, getCourseStructureStudent, refreshUTMCoins, getStudentStreak]);


  const extractOptions = (a) => {
    let op = a?.opciones;
    if (typeof op === "string") {
      try {
        op = JSON.parse(op);
      } catch { }
    }
    if (Array.isArray(op)) return op;
    if (Array.isArray(op?.opciones)) return op.opciones;
    if (Array.isArray(op?.options)) return op.options;
    if (Array.isArray(op?.items)) return op.items;
    return [];
  };

  const toId = (v) => (Number.isNaN(Number(v)) ? String(v) : Number(v));

  const optionTextMap = (a) => {
    const list = extractOptions(a);
    const map = new Map();
    list.forEach((it, idx) => {
      const id = toId(it?.id ?? idx + 1);
      const texto = it?.texto ?? it?.label ?? String(it?.value ?? "");
      map.set(id, texto);
    });
    return map;
  };

  const toDisplay = (tipo, value, a) => {
    const t = (tipo || "").toLowerCase();
    const map = optionTextMap(a);
    const join = (arr) => arr.filter(Boolean).join(", ");

    if (t === "verdadero_falso") {
      if (typeof value === "boolean") return value ? "Verdadero" : "Falso";
      const s = String(value).toLowerCase();
      if (s === "true") return "Verdadero";
      if (s === "false") return "Falso";
      return String(value ?? "—");
    }

    if (t === "opcion_multiple") {
      const id = toId(value?.correcta ?? value);
      return map.get(id) ?? String(value ?? "—");
    }

    if (t === "seleccion_multiple" || t === "multi_seleccion") {
      const arr = Array.isArray(value?.correctas) ? value.correctas : Array.isArray(value) ? value : value == null ? [] : [value];
      const labels = arr.map((v) => map.get(toId(v)) ?? String(v));
      return labels.length ? join(labels) : "—";
    }

    if (t === "completar" || t === "respuesta_corta") {
      if (value && typeof value === "object" && !Array.isArray(value)) {
        try {
          return JSON.stringify(value);
        } catch {
          return String(value);
        }
      }
      if (Array.isArray(value)) return join(value.map(String));
      return String(value ?? "—");
    }

    return String(value ?? "—");
  };

  // auto-report + refresco post-completado
  useEffect(() => {
    if (!done || isReview || showCorrectToast) return;
    if (completionSent) return;
    if (wasCompletedBefore) return;
    if (!curso || !tarea) return;

    (async () => {
      try {
        setSending(true);
        const result = await sendTaskCompletion();
        await getStudentStreak?.().catch(() => { });

        try {
          window.dispatchEvent(
            new CustomEvent("courseFlagsUpdated", {
              detail: { codigoCurso }
            })
          );
        } catch { }

        if (getTaskSubmission) {
          try {
            const submission = await getTaskSubmission(curso.id, Number(tareaId));
            if (Array.isArray(submission?.answers) && submission.answers.length) {
              const map = {};
              activities.forEach((a) => {
                const s =
                  submission.answers.find(
                    (x) => Number(x.actividad_id) === Number(a.estructura_pregunta_id ?? a.id)
                  ) || {};

                const tipo = s.tipo_pregunta ?? a.tipo_pregunta;
                const expected = s.expected ?? a.expected ?? null;
                const respuesta = s.respuesta ?? a.respuesta ?? null;

                const expectedText = toDisplay(tipo, expected, a);
                const answerText = toDisplay(tipo, respuesta, a);

                const correct = compareByType(tipo, expected, respuesta);

                map[a.id] = {
                  correct: !!correct,
                  attempts: 1,
                  answer: respuesta,
                  answerText,
                  expected,
                  expectedText,
                  meta: {
                    tipo_pregunta: tipo,
                    puntos: s.puntos ?? a.puntos ?? 1,
                    explicacion: s.explicacion ?? a.explicacion ?? "",
                  },
                };
              });

              setResults((prev) => {
                const merged = { ...prev };
                for (const [id, entry] of Object.entries(map)) {
                  const prevEntry = prev[id] || {};
                  merged[id] = {
                    ...prevEntry,
                    ...entry,
                    correct: prevEntry.correct || entry.correct,
                    attempts: prevEntry.attempts ?? entry.attempts ?? 1,
                  };
                }
                return merged;
              });

              setQueue([]);
            }
          } catch (e) {
            console.warn("No se pudo actualizar la revisión con getTaskSubmission:", e);
          }
        }

        const coinsAwarded =
          (typeof result?.coins_awarded === "number" ? result.coins_awarded : null) ??
          (typeof result?.utmcoins_ganadas === "number" ? result.utmcoins_ganadas : null) ??
          (typeof result?.coins === "number" ? result.coins : null) ??
          (typeof result?.utmcoins === "number" ? result.utmcoins : null) ??
          0;

        // Fallback LOCAL (por si aún no actualizas backend):
        let finalCoins = Number(coinsAwarded) || 0;

        const base =
          (typeof result?.coins_base === "number" ? result.coins_base : null) ??
          (Number.isFinite(Number(baseReward)) ? Number(baseReward) : 0);

        const att = Number.isFinite(Number(attemptNo)) ? Number(attemptNo) : null;
        const comod = Number.isFinite(Number(controls?.estado?.comodines)) ? Number(controls.estado.comodines) : null;

        if (!Number.isFinite(Number(result?.coins_awarded)) && base > 0) {
          // regla local exacta: 1er intento => *2.5, sino sin comodines => *0.5, sino *1
          const mult = att === 1 ? 2.5 : (comod != null && comod <= 0 ? 0.5 : 1);
          finalCoins = Math.max(0, Math.round(base * mult));
        }

        const firstTime =
          typeof result?.primera_vez === "boolean"
            ? result.primera_vez
            : typeof result?.first_time === "boolean"
              ? result.first_time
              : true;

        if (finalCoins > 0 && firstTime) {
          setCoinsEarned(finalCoins);
          setShowReward(true);
        }

      } catch (e) {
        console.error("Error al reportar finalización:", e);
      } finally {
        setSending(false);
      }
    })();
  }, [
    done,
    isReview,
    showCorrectToast,
    completionSent,
    wasCompletedBefore,
    curso,
    tarea,
    sendTaskCompletion,
    getTaskSubmission,
    activities,
    tareaId,
  ]);

  useEffect(
    () => () => {
      if (correctToastTimerRef.current) clearTimeout(correctToastTimerRef.current);
    },
    []
  );

  /* ============================ carga inicial ============================ */
  useEffect(() => {
    if (hasLoadedRef.current) return;
    hasLoadedRef.current = true;

    (async () => {
      setLoading(true);
      try {
        // 1. Buscar curso del estudiante por código
        const foundCourse = await resolveStudentCourseByCode(codigoCurso);

        if (!foundCourse?.id) {
          throw new Error("Curso no encontrado o no inscrito para este estudiante");
        }

        setCurso(foundCourse);

        // 2. Traer estructura estudiante
        const structureData = await getCourseStructureStudent(foundCourse.id);
        const struct = structureData.structure || [];

        // 3. Localizar la tarea dentro del árbol
        const tid = isNaN(Number(tareaId)) ? tareaId : Number(tareaId);
        const tareaNode = struct
          .flatMap((n) => n.children || [])
          .flatMap((m) => m.children || [])
          .flatMap((comp) => comp.children || [])
          .find((t) => String(t.id) === String(tid) && t.tipo === "tarea");

        if (!tareaNode) throw new Error("Tarea no encontrada");
        setTarea(tareaNode);

        // ✅ intenta leer recompensa base si viene en el nodo (ajusta el campo si tu DB usa otro nombre)
        setBaseReward(Number(
          tareaNode?.recompensa_utmcoins ??
          tareaNode?.utmcoins_recompensa ??
          tareaNode?.reward_coins ??
          tareaNode?.coins_reward ??
          0
        ));

        // ✅ intento actual (preferimos el valor retornado por startTaskAttempt)
        if (!isReview && !wasCompletedBefore) {
          try {
            const at = await startTaskAttempt?.({ curso_id: foundCourse.id, tarea_id: Number(tareaNode.id) });
            const n = Number(
              at?.intentos ??
              at?.intento ??
              at?.attempt_no ??
              at?.attemptNo ??
              at?.numero_intento ??
              at?.attempt ??
              at?.try_no
            );

            if (Number.isFinite(n) && n > 0) setAttemptNo(n);

            if (Number.isFinite(n) && n > 0) setAttemptNo(n);
          } catch (e) {
            console.warn("No se pudo registrar intento:", e);
          }
        }


        // 4. Actividades de la tarea (barajar solo en modo "start")
        const rawActs = collectTaskActivities(tareaNode);
        const acts = isReview ? rawActs : shuffleArray(rawActs);
        setActivities(acts);

        // 5. ID del componente padre (donde vive la tarea)
        const compId = findComponentIdForTask(struct, tid);
        const ownerCompId = componentId ?? compId;

        // 6. Encontrar componente padre y tareas hermanas
        let next = null;
        try {
          const compNode = struct
            .flatMap((n) => n.children || [])
            .flatMap((m) => m.children || [])
            .find((c) => String(c.id) === String(ownerCompId));

          const tareasList = (compNode?.children || []).filter((t) => t.tipo === "tarea");
          const idx = tareasList.findIndex((t) => String(t.id) === String(tid));

          // ⭐ Bloquear SOLO si es acceso directo (sin state). Si viene desde ComponenteTareas, permitir.
          if (!isReview && !cameFromComponent && compNode && idx > 0) {
            const rawSeq = compNode.secuencia_hijos ?? compNode.secuencial_hijos;
            const isSequentialTasks = flagToBoolean(rawSeq, false);

            if (isSequentialTasks) {
              const currentDone = isElementCompleted?.(foundCourse.id, tareaNode.id);

              if (!currentDone) {
                const allPrevCompleted = tareasList
                  .slice(0, idx)
                  .every((t) => isElementCompleted?.(foundCourse.id, t.id));

                if (!allPrevCompleted) {
                  navigate(`/curso/${codigoCurso}/tarea/${ownerCompId}`, {
                    replace: true,
                    state: { blockedTaskId: tareaNode.id },
                  });
                  return;
                }
              }
            }
          }


          // Siguiente tarea (misma lógica que antes)
          if (idx >= 0 && idx + 1 < tareasList.length) {
            next = tareasList[idx + 1];
          }
        } catch { }

        // Guardamos componentId definitivo y siguiente tarea
        setComponentId((prevVal) => prevVal ?? ownerCompId);
        setNextTask(next);

        // ✅ Asegura vidas al entrar (si están en 0 => reset a 3)
        try {
          await ensureLivesOnEnter(ownerCompId);
        } catch { /* no-op */ }


        // 7. Si estamos en modo Revisión, precargamos respuestas
        if (isReview && getTaskSubmission) {
          try {
            const submission = await getTaskSubmission(foundCourse.id, Number(tareaId));
            if (Array.isArray(submission?.answers) && submission.answers.length) {
              const map = {};
              acts.forEach((a) => {
                const s =
                  submission.answers.find(
                    (x) => Number(x.actividad_id) === Number(a.estructura_pregunta_id ?? a.id)
                  ) || {};

                const tipo = s.tipo_pregunta ?? a.tipo_pregunta;
                const expected = s.expected ?? a.expected ?? null;
                const respuesta = s.respuesta ?? a.respuesta ?? null;

                const expectedText = toDisplay(tipo, expected, a);
                const answerText = toDisplay(tipo, respuesta, a);

                const correct = compareByType(tipo, expected, respuesta);

                map[a.id] = {
                  correct: !!correct,
                  attempts: 1,
                  answer: respuesta,
                  answerText,
                  expected,
                  expectedText,
                  meta: {
                    tipo_pregunta: tipo,
                    puntos: s.puntos ?? a.puntos ?? 1,
                    explicacion: s.explicacion ?? a.explicacion ?? "",
                  },
                };
              });

              setResults(map);
              setQueue([]); // review mode => no cola
              setLoading(false);
              return;
            }
          } catch (e) {
            console.error("Error cargando review:", e);
          }
        }

        // 8. Modo normal (start): inicializar resultados + cola
        const init = {};
        acts.forEach((a) => {
          init[a.id] = {
            correct: !!isElementCompleted?.(foundCourse.id, a.id),
            attempts: 0,
            answer: null,
          };
        });
        const initialQueue = acts
          .map((a, idx) => ({ a, idx }))
          .filter(({ a }) => !init[a.id].correct)
          .map(({ idx }) => idx);

        setResults(init);
        setQueue(initialQueue);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [codigoCurso, tareaId, isReview]);


  useEffect(() => {
    setFooterVisible?.(false);
    return () => setFooterVisible?.(true); // para que vuelva en otras pantallas
  }, [setFooterVisible]);

  // ============================== SALIDA / BLOQUEO ==============================
  const [canLeaveSafely, setCanLeaveSafely] = useState(isReview || wasCompletedBefore || false);

  useEffect(() => {
    if (isReview || wasCompletedBefore || done || completionSent) {
      setCanLeaveSafely(true);
      setConfirmExitOpen(false);
    }
  }, [isReview, wasCompletedBefore, done, completionSent]);

  const canLeaveSafelyRef = useRef(canLeaveSafely);
  useEffect(() => {
    canLeaveSafelyRef.current = canLeaveSafely;
  }, [canLeaveSafely]);

  const leaveNow = useCallback(async () => {
    isTryingHardLeaveRef.current = true;

    if (!isReview && done && !completionSent && !wasCompletedBefore && curso && tarea) {
      try {
        setSending(true);
        await sendTaskCompletion();
      } finally {
        setSending(false);
      }
    }

    if (componentId) {
      navigate(`/curso/${codigoCurso}/tarea/${componentId}`, { replace: true });
    } else {
      navigate(-1);
    }
  }, [
    isReview,
    done,
    completionSent,
    wasCompletedBefore,
    curso,
    tarea,
    componentId,
    navigate,
    codigoCurso,
    sendTaskCompletion,
  ]);

  const closeLivesModals = useCallback(() => {
    controls?.setModals?.((m) => ({
      ...m,
      noLives: false,
      noLivesUseComodin: false,
      noLivesNoComodines: false,
    }));
  }, [controls]);


  const leaveNowHard = useCallback(async () => {
    isTryingHardLeaveRef.current = true;

    setCanLeaveSafely(true);
    setConfirmExitOpen(false);

    closeLivesModals();

    if (componentId) {
      navigate(`/curso/${codigoCurso}/tarea/${componentId}`, { replace: true });
    } else {
      navigate(-1);
    }
  }, [componentId, navigate, codigoCurso, closeLivesModals]);




  const attemptLeave = useCallback(() => {
    if (canLeaveSafelyRef.current) {
      leaveNow();
    } else {
      setConfirmExitOpen(true);
    }
  }, [leaveNow]);

  useEffect(() => {
    if (popSetupRef.current) return;
    popSetupRef.current = true;

    const pushBlockState = () => {
      try {
        window.history.pushState({ rtBlock: true }, "");
      } catch { }
    };

    pushBlockState();

    const onPopState = () => {
      if (isTryingHardLeaveRef.current) return;

      if (canLeaveSafelyRef.current) {
        leaveNow();
        return;
      }

      setConfirmExitOpen(true);
      pushBlockState();
    };

    window.addEventListener("popstate", onPopState);
    return () => {
      window.removeEventListener("popstate", onPopState);
    };
  }, [leaveNow]);

  useEffect(() => {
    const handleBeforeUnload = (event) => {
      if (isTryingHardLeaveRef.current) return;
      if (!canLeaveSafelyRef.current) {
        event.preventDefault();
        event.returnValue = "";
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, []);

  /* ============================== interacción ============================== */
  const currentIndex = queue[0] ?? null;
  const currentActivity = currentIndex != null ? activities[currentIndex] : null;


  useEffect(() => {
    const v = controls?.estado?.vidas ?? 0;
    const c = controls?.estado?.comodines ?? 0;

    if (v <= 0 && c <= 0) {
      // ✅ Solo bloquear/sugerir salida si NO hay comodines
      setCanLeaveSafely(true);
      setConfirmExitOpen(false);
      setHardLockNoLives(true);
    } else {
      // ❌ No bloquear si hay comodines disponibles
      setHardLockNoLives(false);
    }
  }, [controls?.estado?.vidas, controls?.estado?.comodines]);


  const handleResult = useCallback(
    (res) => {
      if (isReview) return;
      if (currentIndex == null || !currentActivity) return;

      const isObj = typeof res === "object" && res !== null;
      const isCorrect = isObj ? !!res.correct : !!res;
      const answer = isObj ? res.answer ?? null : results[currentActivity.id]?.answer ?? null;
      const meta = isObj ? res.meta ?? null : results[currentActivity.id]?.meta ?? null;

      setResults((prev) => ({
        ...prev,
        [currentActivity.id]: {
          correct: prev[currentActivity.id]?.correct || isCorrect,
          attempts: (prev[currentActivity.id]?.attempts ?? 0) + 1,
          answer,
          meta,
        },
      }));

      if (isCorrect) {
        const pendingCount = queue.length; // actividades en cola (incluyendo la actual)
        const isLastActivity = pendingCount === 1; // si solo queda 1, es la última

        // ✅ NUEVO: Solo mostrar el toast si NO es la última
        if (!isLastActivity) {
          setShowCorrectToast(true); // modal de "Respuesta correcta" + auto-avance
        } else {
          // ✅ Es la última: avanzar directamente SIN toast
          // El useEffect de auto-report detectará done=true y mostrará el modal de recompensa
          advanceAfterCorrect();
        }
      } else {
        controls.loseLife(); // resta 1 vida al fallar
      }
    },
    [isReview, currentIndex, currentActivity, results, controls, queue.length, advanceAfterCorrect]
  );


  const handleGoNextTask = useCallback(
    async () => {
      if (isReview || !nextTask) return;
      try {
        if (done && !completionSent && !wasCompletedBefore && curso && tarea) {
          setSending(true);
          await sendTaskCompletion();
        }
      } finally {
        setSending(false);
      }
      const targetUrl = `/curso/${codigoCurso}/tarea/${nextTask.id}/resolver`;
      window.location.assign(targetUrl);
    },
    [isReview, nextTask, done, completionSent, wasCompletedBefore, curso, tarea, sendTaskCompletion, codigoCurso]
  );

  /* ============================== Render ============================== */

  /* ===================== Modal estilo "INTÉNTALO DE NUEVO" (reutilizable) ===================== */
  function TryAgainStyleModal({
    open,
    header = "AVISO",
    title = "",
    message = null,
    primaryLabel = "Entendido",
    onPrimary,
    secondaryLabel = null,
    onSecondary,
    tone = "warn", // "warn" | "danger"
  }) {
    if (!open) return null;

    const iconToneClass = tone === "danger" ? "rtm-icon-danger" : "rtm-icon-warn";

    return (
      <div className="rtm-overlay" role="dialog" aria-modal="true" aria-label={header}>
        <div className="rtm-card">
          <div className="rtm-head">{header}</div>

          <div className="rtm-body">
            <div className={`rtm-icon ${iconToneClass}`} aria-hidden="true">
              <AlertTriangle size={26} />
            </div>

            <div className="rtm-title">{title}</div>

            {message && <div className="rtm-text">{message}</div>}
          </div>

          <div className="rtm-actions">
            {secondaryLabel && (
              <button type="button" className="rtm-btn rtm-btn-danger" onClick={onSecondary}>
                {secondaryLabel}
              </button>
            )}

            <button type="button" className="rtm-btn rtm-btn-primary" onClick={onPrimary}>
              {primaryLabel}
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (loading) return <LoadingSpinner message="Cargando tarea…" />;

  if (!curso || !tarea) {
    return (
      <div className="rt-root">
        {RT_STYLES}
        <div className="rt-empty">
          <span className="rt-dim">No se pudo cargar la tarea.</span>
        </div>
      </div>
    );
  }

  return (
    <div className="rt-root">
      {RT_STYLES}

      {/* Header móvil con botón volver y título corto */}
      <div className="rt-header-mobile">
        <button onClick={attemptLeave} disabled={sending} className="rt-btn rt-btn-back">
          <ArrowLeft size={16} />
          <span>{sending ? "Guardando…" : "Volver"}</span>
        </button>

        <div className="rt-header-mobile-info">
          <div className="rt-eyebrow rt-eyebrow-blue">Tarea</div>
          <div className="rt-mobile-title">
            {tarea.nombre}
            {isReview ? " — Revisión" : ""}
          </div>
        </div>
      </div>

      <div className="rt-container">
        {/* ===== SIDEBAR ===== */}
        <aside className="rt-sidebar">
          {/* Topbar / volver (solo desktop) */}
          <div className="rt-topbar">
            <button onClick={attemptLeave} disabled={sending} className="rt-btn rt-btn-back">
              <ArrowLeft size={16} />
              <span>{sending ? "Guardando…" : "Volver"}</span>
            </button>
          </div>

          <TaskInfoCard tarea={tarea} isReview={isReview} />
          <ProgressBlock
            total={total}
            correctCount={correctCount}
            pct={pct}
            activities={activities}
            queue={queue}
            results={results}
            isReview={isReview}
            questionCount={questionCount}
            gameCount={gameCount}
            lives={controls.estado.vidas}         // 👈 NUEVO
          />

          <ComodinBlock
            isReview={isReview}
            comodinLoading={comodinLoading}
            comodin={comodin}
            canBuyComodin={canBuyComodin}
            buyingComodin={buyingComodin}
            setConfirmBuyOpen={setConfirmBuyOpen}
            coinsNow={coinsNow}
            comodinMsg={comodinMsg}
          />
          {/* (Se eliminó KPIsBlock por solicitud) */}
        </aside>

        {/* ===== CONTENIDO PRINCIPAL ===== */}
        <main className="rt-main">
          {!isReview ? (
            done ? (
              <div className="rt-done">
                <div className="rt-done-icon">
                  <CheckCircle size={36} />
                </div>

                <h3 className="rt-done-title">¡Tarea Completada!</h3>

                <p className="rt-done-desc">Todas las actividades fueron respondidas correctamente.</p>

                <div className="rt-buttons" style={{ marginTop: 8 }}>
                  <button onClick={attemptLeave} className="rt-btn rt-btn-light">
                    Volver al Componente
                  </button>
                  {!!nextTask && (
                    <button onClick={handleGoNextTask} className="rt-btn rt-btn-success rt-btn-inline">
                      Siguiente tarea <ChevronRight size={16} />
                    </button>
                  )}
                </div>
              </div>
            ) : currentActivity ? (
              <ActivityRunner
                key={currentActivity?.id}
                activity={currentActivity}
                onResult={handleResult}
                componentId={componentId}
                comodinesRefreshKey={comodinesRefreshKey}
                showInlineCorrect={showCorrectToast}
                openComodinesSignal={openComodinesSignal}
                controls={{ ...controls, goBack: attemptLeave }}
              />

            ) : (
              <div className="rt-empty">
                <span className="rt-dim">Cargando actividad…</span>
              </div>
            )
          ) : (
            <ReviewAnswers activities={activities} results={results} />
          )}
        </main>
      </div>

      {showCorrectToast && (
        <CorrectModal
          autoMs={AUTO_ADVANCE_MS}
          onContinue={() => {
            setShowCorrectToast(false);
            advanceAfterCorrect();
          }}
        />
      )}
      {showReward && !showCorrectToast && (
        <RewardOverlay
          coins={coinsEarned}
          onBack={attemptLeave}
          onNext={handleGoNextTask}
          hasNext={!!nextTask && !isReview}
          onClose={attemptLeave}
        />
      )}

      {/* Modal de confirmación de salida */}
      <ConfirmExitModal
        open={confirmExitOpen}
        title="¿Salir de la tarea?"
        message={done ? "Vas a salir de esta tarea." : "Todavía no has terminado. ¿Seguro que quieres salir?"}
        cancelLabel="Seguir aquí"
        confirmLabel="Salir ahora"
        onCancel={() => setConfirmExitOpen(false)}
        onConfirm={async () => {
          setConfirmExitOpen(false);
          await leaveNow();
        }}
      />

      {comodin && (
        <BuyComodinModal
          open={confirmBuyOpen}
          item={comodin}
          coinsNow={coinsNow}
          confirming={buyingComodin}
          canBuy={canBuyComodin}
          onCancel={() => {
            setConfirmBuyOpen(false);
            if (hardLockNoLives || controls?.estado?.vidas <= 0) {
              // Canceló la compra sin vidas: salir al componente
              leaveNow();
            }
          }}
          onConfirm={async () => {
            await buyOneComodin();
            setConfirmBuyOpen(false);
          }}
        />
      )}

      {/* ===== Modales centralizados para skip/retry/coins ===== */}
      {controls.modals.skipBlocked && (
        <div className="cxm-overlay">
          <div className="cxm-card">
            <div className="cxm-ribbon">Acción sensible</div>
            <h4 className="cxm-title">¿Saltar actividad bloqueada?</h4>
            <p className="cxm-msg">
              Si saltas estando bloqueado, <b>todas las preguntas</b> quedarán bloqueadas hasta que uses un comodín.
            </p>
            <div className="cxm-actions">
              <button className="rt-btn cxm-btn-cancel" onClick={() => controls.setModals(m => ({ ...m, skipBlocked: false }))}>
                Cancelar
              </button>
              <button className="rt-btn cxm-btn-confirm" onClick={controls.confirmSkipBlocked}>
                Saltar y bloquear todo
              </button>
            </div>
          </div>
        </div>
      )}

      {controls.modals.noComodines && (
        <div className="cxm-overlay">
          <div className="cxm-card">
            <div className="cxm-ribbon" style={{ background: 'linear-gradient(90deg,#f59e0b,#fbbf24)' }}>Aviso</div>
            <h4 className="cxm-title">Comodines agotados</h4>
            <p className="cxm-msg">No puedes usar <b>Vida extra</b> ni <b>Pista</b> en esta tarea.</p>
            <div className="cxm-actions">
              <button className="rt-btn cxm-btn-cancel" onClick={() => controls.setModals(m => ({ ...m, noComodines: false }))}>
                Entendido
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ===================== Intento #1: vidas agotadas ===================== */}
      <TryAgainStyleModal
        open={!!controls?.modals?.noLives}
        header="VIDAS AGOTADAS"
        title="Te has quedado sin vidas"
        message={
          <>
            Has fallado 3 veces y agotado tus vidas en este primer intento.
            <br />
            Si sales perderas la bonificacion de monedas, ¿Quieres salir o usar un comodin?.
          </>
        }
        primaryLabel="Comodines"
        onPrimary={() => {
          // cerrar modales de vidas
          closeLivesModals();

          // abrir el menú real de comodines en PreguntaRenderer
          setOpenComodinesSignal((s) => s + 1);

          // opcional: resaltar el botón comodines si lo manejas
          controls?.setCtaComodines?.(true);
        }}
        secondaryLabel="Rendirse"
        onSecondary={leaveNowHard}
        tone="warn"
      />

      {/* ===================== Intento #2+ con comodines: vidas agotadas + usar comodín ===================== */}
      <TryAgainStyleModal
        open={!!controls?.modals?.noLivesUseComodin}
        header="VIDAS AGOTADAS"
        title="Te has quedado sin vidas"
        message={
          <>
            Aún tienes <b>comodines</b> disponibles.
            <br />
            Pulsa <b>Comodines</b> para elegir uno y continuar.
          </>
        }
        primaryLabel="Comodines"
        onPrimary={() => {
          // cerrar modales de vidas
          closeLivesModals();

          // abrir el menú real de comodines en PreguntaRenderer
          setOpenComodinesSignal((s) => s + 1);

          // opcional: resaltar el botón comodines si lo manejas
          controls?.setCtaComodines?.(true);
        }}
        secondaryLabel="Rendirse"
        onSecondary={leaveNowHard}
        tone="warn"
      />



      {/* ===================== Sin vidas y sin comodines: solo salir ===================== */}
      <TryAgainStyleModal
        open={!!controls?.modals?.noLivesNoComodines}
        header="VIDAS AGOTADAS"
        title="Te has quedado sin vidas"
        message={
          <>
            No tienes vidas ni comodines disponibles.
            <br />
            No puedes continuar con esta tarea.
          </>
        }
        primaryLabel="Salir de la tarea"
        onPrimary={leaveNowHard}
        tone="danger"
      />

      {tourOpen && TOUR_STEPS[tourStep] && (
        <TutorialOverlay
          step={TOUR_STEPS[tourStep]}
          currentStep={tourStep + 1}
          totalSteps={TOUR_STEPS.length}
          onNext={nextTourStep}
          onPrev={prevTourStep}
          onClose={closeTour}
          onSkip={skipTour}
        />
      )}




      {RT_STYLES}
    </div>
  );
}

/* ================= Renderers puente ================= */
function ActivityRunner({ activity, onResult, componentId, comodinesRefreshKey, showInlineCorrect, controls, openComodinesSignal }) {
  const isGame = activity?.tipo_actividad === "juego";
  const isQuestion = activity?.tipo_actividad === "pregunta";

  if (isGame) {
    return (
      <JuegoRenderer
        game={activity}
        onResult={onResult}
        componentId={componentId}
        comodinesRefreshKey={comodinesRefreshKey}
        showInlineCorrect={showInlineCorrect}
        controls={controls}                 // ✅ NUEVO
      />
    );
  }

  if (isQuestion) {
    return (
      <PreguntaRenderer
        question={activity}
        onResult={onResult}
        componentId={componentId}
        comodinesRefreshKey={comodinesRefreshKey}
        showInlineCorrect={showInlineCorrect}
        controls={controls}
        openComodinesSignal={openComodinesSignal}   // ✅ NUEVO
      />

    );
  }

  return (
    <div className="rt-fallback">
      <div className="rt-eyebrow rt-eyebrow-blue">Actividad</div>
      <h3 className="rt-title-sm">{activity?.nombre || activity?.titulo || "Actividad"}</h3>
      <p className="rt-desc">Renderer pendiente de implementar.</p>
      <div className="rt-buttons">
        <button onClick={() => onResult(true)} className="rt-btn rt-btn-success">
          Marcar Correcto
        </button>
        <button onClick={() => onResult(false)} className="rt-btn rt-btn-light">
          Marcar Incorrecto
        </button>
      </div>
    </div>
  );
}


function ReviewAnswers({ activities, results }) {
  const formatValue = (v) => {
    if (v == null || v === "") return "—";
    if (Array.isArray(v)) return v.join(", ");
    if (typeof v === "object") {
      try {
        return JSON.stringify(v);
      } catch {
        return String(v);
      }
    }
    return String(v);
  };

  return (
    <div className="rt-review">
      {activities.map((a, idx) => {
        const r = results[a.id] || {};
        const showAnswer = r.answerText ?? formatValue(r.answer);
        const showExpected = r.expectedText ?? formatValue(r.expected);

        return (
          <div key={a.id} className={`rt-review-card ${r.correct ? "rt-review-ok" : ""}`}>
            <div className="rt-row rt-row-between">
              <strong className="rt-review-strong">Actividad {idx + 1}: {a.titulo || a.nombre || a.tipo_actividad}</strong>
              {r.correct && <span className="rt-dot rt-dot-ok" />}
            </div>

            <div className="rt-review-grid">
              <div className="rt-review-col">
                <div className="rt-eyebrow rt-eyebrow-blue">Tu respuesta</div>
                <div className="rt-review-val">{showAnswer}</div>
              </div>
              <div className="rt-review-col">
                <div className="rt-eyebrow rt-eyebrow-blue">Respuesta correcta</div>
                <div className="rt-review-val">{showExpected}</div>
              </div>

              {r.meta?.explicacion && (
                <div className="rt-explain">
                  <div className="rt-eyebrow rt-eyebrow-blue">Explicación</div>
                  <div className="rt-explain-text">{r.meta.explicacion}</div>
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* ============================= Estilos unificados ============================= */
const RT_STYLES = (
  <style>{`
  :root {
    --rt-bg-page: #f8fafc;
    --rt-card-bg: #ffffff;
    --rt-card-border: #e5e7eb;
    --rt-card-radius: 14px;
    --rt-text-main: #0f172a;
    --rt-text-dim: #64748b;
    --rt-blue-main: #0f5fb8;
    --rt-blue-bg: #eef5ff;
    --rt-blue-border: #bfdbfe;
    --rt-green-main: #16a34a;
    --rt-green-bg: #ecfdf5;
    --rt-green-border: #bbf7d0;
    --rt-purple-bg: #f5f3ff;
    --rt-purple-border: #ddd6fe;
    --rt-purple-main: #6d28d9;
    --rt-shadow-card: 0 6px 24px rgba(0,0,0,0.06);
    --rt-shadow-hover: 0 10px 30px rgba(0,0,0,0.08);
    --rt-radius-pill: 9999px;
  }

  /* ROOT LAYOUT */
  .rt-root {
    background: var(--rt-bg-page);
    min-height: calc(100vh - var(--footer-h, 0px));
    padding: 16px clamp(12px, 2vw, 20px);
    color: var(--rt-text-main);
    display: flex;
    flex-direction: column;
  }

  /* header compacto (visible solo en móvil) */
  .rt-header-mobile {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 16px;
  }
  .rt-header-mobile-info { flex:1; min-width:0; }
  .rt-mobile-title{
    font-weight:700; font-size:1rem; color:var(--rt-text-main);
    line-height:1.2; word-break:break-word;
  }
  .rt-eyebrow {
    font-size: .7rem; text-transform: uppercase; font-weight:600;
    letter-spacing:.08em; color: var(--rt-text-dim);
  }
  .rt-eyebrow-blue { color: var(--rt-blue-main); }

  /* ========================= GRID POR ÁREAS ========================= */
  .rt-container {
    max-width: 1280px;
    width: 100%;
    margin: 0 auto;
    display: grid;
    column-gap: clamp(16px,2vw,24px);
    row-gap: 16px;
    grid-template-columns: 320px minmax(0,1fr);
    grid-template-areas:
      "sidebar main";
    align-items: start;
    flex: 1 1 auto;
    min-height: 0;
  }
  .rt-sidebar { grid-area: sidebar; }
  .rt-main { grid-area: main; }

  /* MAIN CONTENT */
  .rt-main {
    background: var(--rt-card-bg);
    border: 1px solid var(--rt-card-border);
    border-radius: var(--rt-card-radius);
    box-shadow: var(--rt-shadow-card);
    padding: 16px;
    min-height: 0;
    min-width: 0;
  }

  /* SIDEBAR */
  .rt-sidebar {
    position: sticky;
    top: 10px;
    align-self: start;
    display: grid;
    gap: 12px;
    height: fit-content;
  }
    .rt-chip-life{
  background:#fee2e2;
  color:#b91c1c;
  border-color:#fecaca;
}
  /* ================= Modal estilo "INTÉNTALO DE NUEVO" ================= */
.rtm-overlay{
  position:fixed;
  inset:0;
  z-index:10050;
  display:grid;
  place-items:center;
  padding:16px;
  background:rgba(0,0,0,.55);
  backdrop-filter: blur(6px);
  animation: cxmFade .2s ease;
}

.rtm-card{
  width:min(92vw, 540px);
  background:#fff;
  border-radius:18px;
  border:1px solid #e5e7eb;
  box-shadow:0 30px 80px rgba(0,0,0,.35);
  overflow:hidden;
  animation: cxmPop .28s cubic-bezier(.16,1,.3,1);
}

.rtm-head{
  background:linear-gradient(90deg,#4f46e5,#7c3aed);
  color:#fff;
  font-weight:900;
  letter-spacing:.06em;
  text-transform:uppercase;
  font-size:.85rem;
  padding:12px 16px;
  text-align:center;
}

.rtm-body{
  padding:22px 18px 10px;
  text-align:center;
}

.rtm-icon{
  width:54px;
  height:54px;
  margin:0 auto 10px;
  border-radius:9999px;
  display:grid;
  place-items:center;
  border:1px solid;
  box-shadow:0 10px 24px rgba(0,0,0,.08);
}

.rtm-icon-warn{
  background: radial-gradient(160px 110px at 50% -20%, rgba(245,158,11,.25), transparent 60%), #fff7ed;
  border-color:#fed7aa;
  color:#f59e0b;
}

.rtm-icon-danger{
  background: radial-gradient(160px 110px at 50% -20%, rgba(239,68,68,.18), transparent 60%), #fff1f2;
  border-color:#fecaca;
  color:#ef4444;
}

.rtm-title{
  font-weight:900;
  color:#0f172a;
  font-size:1.05rem;
  margin:6px 0 8px;
}

.rtm-text{
  color:#64748b;
  font-size:.95rem;
  line-height:1.45;
  margin:0 auto 6px;
  max-width:420px;
}

.rtm-actions{
  display:flex;
  justify-content:center;
  gap:12px;
  padding:14px 18px 18px;
  flex-wrap:wrap;
}

.rtm-btn{
  border:none;
  border-radius:12px;
  padding:10px 16px;
  font-weight:800;
  cursor:pointer;
  min-width:140px;
  transition: transform .12s ease, filter .15s ease, box-shadow .15s ease;
}

.rtm-btn:hover{
  transform: translateY(-1px);
  filter: brightness(1.03);
}

.rtm-btn-primary{
  background:linear-gradient(90deg,#4f46e5,#7c3aed);
  color:#fff;
  box-shadow:0 12px 26px rgba(99,102,241,.28);
}

.rtm-btn-danger{
  background:linear-gradient(90deg,#ef4444,#dc2626);
  color:#fff;
  box-shadow:0 12px 26px rgba(239,68,68,.22);
}


  /* Top bar (volver) solo desktop */
  .rt-topbar { display:flex; justify-content:flex-start; }

  /* CARD BASE */
  .rt-card {
    background: var(--rt-card-bg);
    border: 1px solid var(--rt-card-border);
    border-radius: var(--rt-card-radius);
    box-shadow: var(--rt-shadow-card);
    padding: 14px 16px;
  }

  .rt-pop { position: relative; overflow: hidden; }
  .rt-pop:before{
    content:""; position:absolute; inset:0; pointer-events:none;
    background: radial-gradient(500px 200px at -10% -20%, rgba(15,95,184,0.10), transparent 70%);
  }

  .rt-title {
    margin: 4px 0 4px; font-size: 1rem; color: var(--rt-text-main);
    font-weight: 800; letter-spacing: .02em; line-height:1.25; word-break: break-word;
  }
  .rt-title-sm { margin: 4px 0; font-size: .95rem; font-weight:700; color:var(--rt-text-main); }
  .rt-desc { margin: 4px 0 0; color: var(--rt-text-dim); font-size:.9rem; line-height:1.35; }

  .rt-row { display:flex; align-items:center; gap:8px; flex-wrap:wrap; }
  .rt-row-between { display:flex; align-items:flex-start; justify-content:space-between; gap:8px; flex-wrap:wrap; }
  .rt-row-gap { gap:8px; }

  .rt-icon-blue { color: var(--rt-blue-main); }
  .rt-strong { font-weight:700; color:var(--rt-text-main); }

  /* BADGES / PILLS */
  .rt-badge {
    padding: 4px 10px; border-radius: var(--rt-radius-pill); border:1px solid var(--rt-card-border);
    background:#f8fafc; font-weight:700; font-size:.75rem; color:var(--rt-text-main);
    line-height:1.2; box-shadow: 0 1px 2px rgba(0,0,0,.04) inset;
  }
  .rt-badge-success { background: var(--rt-green-bg); border-color: var(--rt-green-border); color: var(--rt-green-main); }

  /* PROGRESS BAR */
  .rt-progress {
    position: relative; height: 10px; border-radius: var(--rt-radius-pill);
    overflow: hidden; border:1px solid var(--rt-card-border); background: #f1f59; margin-top: 10px;
  }
  .rt-progress-bar {
    position:absolute; inset:0; width:0; background: linear-gradient(90deg,#4f46e5,#7c3aed);
    animation: rtGrow .45s ease;
  }
  .rt-progress-stripes {
    position:absolute; inset:0; background-size:20px 20px;
    background-image:linear-gradient(45deg,rgba(255,255,255,.4) 25%,transparent 25%,transparent 50%,rgba(255,255,255,.4) 50%,rgba(255,255,255,.4) 75%,transparent 75%,transparent);
    mix-blend-mode: soft-light; animation: rtMove 1.2s linear infinite; pointer-events:none;
  }

  /* DOTS */
  .rt-dots { display:flex; flex-wrap:wrap; gap:6px; margin-top:12px; }
  .rt-dot { width:12px; height:12px; border-radius:var(--rt-radius-pill); background:#e2e8f0; border:1px solid var(--rt-card-border); }
  .rt-dot-ok { background: var(--rt-green-bg); border-color: var(--rt-green-border); box-shadow:0 0 0 2px rgba(22,163,74,.15); }
  .rt-dot-current { outline:2px solid var(--rt-blue-main); box-shadow:0 0 0 2px rgba(15,95,184,.25); }

  /* Chips */
  .rt-chips{ display:flex; align-items:center; gap:8px; flex-wrap:wrap; }
  .rt-chip{
    display:inline-flex; align-items:center; gap:6px; padding:4px 8px;
    border-radius:9999px; border:1px solid; font-size:.85rem; line-height:1; white-space:nowrap;
  }
  .rt-chip svg{ width:14px; height:14px; }
  .rt-chip-q{ background:#fff7ed; color:#c2410c; border-color:#fed7aa; }
  .rt-chip-g{ background:#f5f3ff; color:#6d28d9; border-color:#ddd6fe; }

  /* BUTTONS */
  .rt-buttons { display:flex; flex-wrap:wrap; row-gap:8px; column-gap:10px; justify-content:center; }
  .rt-btn {
    position:relative; overflow:hidden; display:inline-flex; align-items:center; justify-content:center;
    gap:8px; padding:10px 14px; border-radius:10px; border:1px solid var(--rt-card-border);
    background:#fff; color:var(--rt-text-main); font-weight:700; font-size:.9rem; cursor:pointer; line-height:1.2;
    transition:transform .12s ease, box-shadow .15s ease, background .15s ease;
  }
  .rt-btn:hover { transform: translateY(-1px); box-shadow: var(--rt-shadow-hover); }
  .rt-btn-primary{ background: linear-gradient(90deg,#4f46e5,#7c3aed); color:#fff; border:none; box-shadow: 0 10px 24px rgba(99,102,241,.18); }
  .rt-btn-primary:hover{ filter:brightness(1.05); }
  .rt-btn-success{ background: var(--rt-green-main); border:none; color:#fff; }
  .rt-btn-light{ background:#f8fafc; border:1px solid var(--rt-card-border); color:#var(--rt-text-main); }
  .rt-btn-ghost{ background:#fff; border:1px solid var(--rt-card-border); color:#var(--rt-text-dim); }
  .rt-btn-inline{ display:inline-flex; align-items:center; }

  .rt-btn-back {
    background:#f8fafc; border:1px solid #e2e8f0; border-radius:10px;
    box-shadow:0 1px 2px rgba(0,0,0,.04) inset; font-weight:600; font-size:.85rem;
    line-height:1.2; color:#0f172a; padding:8px 12px;
  }


/* Secciones del tutorial */
.tour-section {
  margin-bottom: 16px;
}

.tour-section:last-child {
  margin-bottom: 0;
}

.tour-section-title {
  margin: 0 0 6px 0;
  font-size: 0.92rem;
  font-weight: 900;
  color: #1e293b;
  display: flex;
  align-items: center;
  gap: 6px;
  letter-spacing: 0.2px;
}

.tour-bullet {
  padding-left: 4px;
  margin-bottom: 10px;
}

.tour-bullet strong {
  color: #2563eb;
  font-weight: 800;
}

/* Scrollbar personalizado para el texto */
.tour-text::-webkit-scrollbar {
  width: 6px;
}

.tour-text::-webkit-scrollbar-track {
  background: #f1f5f9;
  border-radius: 10px;
}

.tour-text::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 10px;
}

.tour-text::-webkit-scrollbar-thumb:hover {
  background: #94a3b8;
}

  /* ====== CorrectModal (blanco + verde) ====== */
.cm-overlay{
  position:fixed; inset:0; z-index:10000;
  display:grid; place-items:center; padding:16px;
  background:rgba(0,0,0,.55); backdrop-filter:blur(6px);
  animation: cxmFade .2s ease;
}
.cm-card{
  width:min(92vw, 420px);
  background:#ffffff; color:var(--rt-text-main);
  border:1px solid var(--rt-green-border);
  border-radius:18px; box-shadow:0 30px 80px rgba(0,0,0,.35);
  padding:18px 16px 14px; text-align:center; animation: cxmPop .28s cubic-bezier(.16,1,.3,1);
}
.cm-icon{
  width:48px; height:48px; border-radius:9999px; margin:0 auto 8px;
  display:flex; align-items:center; justify-content:center;
  background:var(--rt-green-bg); color:var(--rt-green-main); border:1px solid var(--rt-green-border);
}
.cm-title{ margin:0; font-size:1.05rem; font-weight:900; color:var(--rt-text-main); }
.cm-sub{ margin:4px 0 10px; font-size:.9rem; color:var(--rt-text-dim); }
.cm-actions{ display:flex; justify-content:center; gap:10px; }
.cm-progress{
  position:relative; height:3px; background:#e5e7eb; border-radius:999px; overflow:hidden; margin:8px 0 12px;
}
.cm-progress::before{
  content:""; position:absolute; inset:0 auto 0 0; width:0;
  background:var(--rt-green-main);
  animation: cmFill var(--cm-ms, 1800ms) linear forwards;
}
@keyframes cmFill { to { width:100%; } }

/* ====== Tarea Completada (blanco + verde) ====== */
.rt-done{
  border:1px solid var(--rt-green-border);
  background:#ffffff; border-radius:16px; padding:20px;
  text-align:center; color:var(--rt-text-main);
  box-shadow: var(--rt-shadow-card);
}
.rt-done-icon{
  width:48px; height:48px; border-radius:9999px; margin:0 auto 6px;
  display:flex; align-items:center; justify-content:center;
  background:var(--rt-green-bg); color:var(--rt-green-main); border:1px solid var(--rt-green-border);
}
.rt-done-title{ margin:0; font-size:1.25rem; font-weight:900; color:var(--rt-text-main); }
.rt-done-desc{ margin:2px 0 0; font-size:.9rem; color:var(--rt-text-dim); }

/* ====== Pequeños fixes ====== */
.rt-btn-light{ color: var(--rt-text-main); }          /* antes tenía un #var... */
.rt-progress { background:#f1f5f9; }                  /* corregido hex inválido */


  /* FALLBACK / EMPTY */
  .rt-fallback, .rt-empty {
    border:1px solid var(--rt-card-border); background:var(--rt-card-bg); border-radius:16px;
    box-shadow:var(--rt-shadow-card); padding:16px; text-align:center; color:var(--rt-text-dim); font-size:.9rem;
  }

  /* REVIEW LIST */
  .rt-review { display:grid; gap:12px; }
  .rt-review-card {
    background: var(--rt-card-bg); border:1px solid var(--rt-card-border); border-radius: var(--rt-card-radius);
    box-shadow: var(--rt-shadow-card); padding:14px 16px; transition: box-shadow .15s ease, transform .15s ease;
  }
  .rt-review-card:hover { box-shadow: var(--rt-shadow-hover); transform: translateY(-1px); }
  .rt-review-ok { background: linear-gradient(135deg,#ffffff 0%,#f1f8e9 100%); border-color: var(--rt-green-border); box-shadow:0 8px 24px rgba(0,0,0,.05); }
  .rt-review-strong { font-weight:600; color:var(--rt-text-main); font-size:.9rem; line-height:1.3; }
  .rt-review-grid { display:grid; gap:12px; margin-top:12px; }
  .rt-review-col .rt-review-val{ font-size:.9rem; color:var(--rt-text-main); line-height:1.4; word-break:break-word; }
  .rt-explain { border-top:1px dashed var(--rt-card-border); padding-top:8px; }
  .rt-explain-text { color:var(--rt-text-dim); font-size:.85rem; line-height:1.4; word-break:break-word; }

  /* ====== Toast correcto + barra ====== */
  .rt-toast{
    position:fixed; left:50%; bottom:22px; transform:translateX(-50%);
    z-index:9999; display:flex; align-items:center; gap:8px;
    padding:10px 14px 14px; border-radius:12px; font-size:.92rem; font-weight:700;
    box-shadow:0 12px 30px rgba(0,0,0,.18); animation: toastIn .18s ease;
  }
  .rt-toast-correct{ background:linear-gradient(135deg,#16a34a,#22c55e); color:#fff; border:1px solid rgba(255,255,255,.2); }
  .rt-toast-sub{ font-weight:600; opacity:.9; }
  .rt-toast-dot{ width:10px; height:10px; border-radius:999px; background:#fff; box-shadow:0 0 0 6px rgba(255,255,255,.18); }
  .rt-toast-progress{
    position:absolute; left:0; right:0; bottom:0; height:3px; background:rgba(255,255,255,.9);
    transform-origin:left; animation: rtToastProgress linear forwards;
  }
  @keyframes rtToastProgress { from{ transform:scaleX(0); } to{ transform:scaleX(1); } }
  @keyframes toastIn { from{ transform:translateX(-50%) translateY(10px); opacity:0 } to{ transform:translateX(-50%) translateY(0); opacity:1 } }

  /* ================= Overlay recompensa ================= */
  .reward-overlay {
    position: fixed; inset: 0; z-index: 9999; display: grid; place-items: center;
    background: radial-gradient(circle at center, rgba(16, 185, 129, 0.15), transparent 70%), rgba(0, 0, 0, 0.85);
    backdrop-filter: blur(8px); animation: rewardFadeIn 0.3s ease; padding:16px;
  }
  .reward-container {
    position: relative; width: min(90vw, 420px); padding: 3rem 2rem;
    background: linear-gradient(135deg, #0a1f0d 0%, #1a3a1f 100%);
    border: 2px solid rgba(16, 185, 129, 0.3); border-radius: 24px;
    box-shadow: 0 25px 50px rgba(0, 0, 0, 0.5), inset 0 1px 0 rgba(255, 255, 255, 0.1), 0 0 60px rgba(16, 185, 129, 0.3);
    animation: rewardBounceIn 0.6s cubic-bezier(0.68, -0.55, 0.265, 1.55); color: #fff; text-align:center;
  }
  .reward-content { position: relative; z-index: 2; text-align: center; }
  .reward-close-btn {
    position:absolute; top:10px; right:10px; padding:4px 8px; font-size:1rem; line-height:1; border-radius:8px;
    background:rgba(255,255,255,0.08); border:1px solid rgba(255,255,255,0.2); color:#fff; font-weight:600;
  }
  .reward-coin-wrapper { position: relative; display: inline-block; margin-bottom: 1.5rem; animation: rewardCoinFloat 1.5s ease-in-out infinite; }
  .reward-coin { display: block; filter: drop-shadow(0 10px 30px rgba(255, 215, 0, 0.4)); animation: rewardCoinRotate 3s linear infinite; }
  .reward-coin-shine {
    position: absolute; inset: 0; background: linear-gradient(90deg, transparent 0%, rgba(255, 255, 255, 0.6) 50%, transparent 100%);
    border-radius: 50%; animation: rewardShine 2s ease-in-out infinite; pointer-events: none;
  }
  .reward-title {
    margin: 0 0 1rem; font-size: 2rem; font-weight: 900;
    background: linear-gradient(135deg, #10b981, #34d399);
    -webkit-background-clip: text; -webkit-text-fill-color: transparent; background-clip: text;
  }
  .reward-amount { display: flex; align-items: center; justify-content: center; gap: 0.5rem; margin-bottom: 1rem; font-size: 3rem; font-weight: 900; color: #ffd700; text-shadow: 0 0 20px rgba(255, 215, 0, 0.6); line-height:1; }
  .reward-plus { font-size: 2rem; opacity: 0.8; color:#ffd700; }
  .reward-value { font-size: 4rem; letter-spacing: -0.02em; color:#ffd700; }
  .reward-label { font-size: 1.2rem; color: #34d399; font-weight: 700; margin-left: 0.25rem; line-height:1.2; }
  .reward-particles { position: absolute; inset: 0; overflow: hidden; border-radius: 24px; pointer-events: none; }
  .reward-particle {
    position: absolute; bottom: 0; left: 50%; width: 8px; height: 8px; background: radial-gradient(circle, #ffd700, #ffa500);
    border-radius: 50%; box-shadow: 0 0 10px rgba(255, 215, 0, 0.8);
    animation: rewardParticleRise forwards; transform: translateX(var(--x)) translateY(var(--y)); opacity: 0;
  }
  .reward-rings { position: absolute; inset: 0; pointer-events: none; }
  .reward-ring {
    position: absolute; top: 50%; left: 50%; width: 100px; height: 100px; margin: -50px 0 0 -50px;
    border: 3px solid rgba(16, 185, 129, 0.6); border-radius: 50%; animation: rewardRingExpand 2s ease-out infinite;
  }
  @keyframes rewardFadeIn { from { opacity: 0; } to { opacity: 1; } }
  @keyframes rewardBounceIn { 0% { opacity: 0; transform: scale(0.3) rotate(-10deg); } 50% { transform: scale(1.05) rotate(2deg); } 70% { transform: scale(0.95) rotate(-1deg); } 100% { opacity: 1; transform: scale(1) rotate(0); } }
  @keyframes rewardCoinFloat { 0%, 100% { transform: translateY(0px); } 50% { transform: translateY(-15px); } }
  @keyframes rewardCoinRotate { 0% { transform: rotateY(0deg); } 100% { transform: rotateY(360deg); } }
  @keyframes rewardShine { 0% { transform: translateX(-200%) rotate(45deg); opacity: 0; } 50% { opacity: 1; } 100% { transform: translateX(200%) rotate(45deg); opacity: 0; } }
  @keyframes rewardParticleRise { 0% { opacity: 0; transform: translateX(var(--x)) translateY(0) scale(0); } 10% { opacity: 1; } 100% { opacity: 0; transform: translateX(var(--x)) translateY(var(--y)) scale(1.5); } }
  @keyframes rewardRingExpand { 0% { transform: scale(1); opacity: 1; } 100% { transform: scale(3); opacity: 0; } }

    /* ====== Variantes grandes para modales (vidas agotadas) ====== */
.cxm-card-lg{
  width: min(92vw, 520px);
  padding: 56px 18px 18px; /* deja espacio para el ribbon */
  border-radius: 22px;
}

.cxm-card-lg-2{
  padding-top: 64px; /* un poco más por el icono */
}

.cxm-ribbon-dark{
  background: linear-gradient(90deg,#4f46e5,#7c3aed);
  box-shadow: 0 10px 24px rgba(99,102,241,.35);
}

.cxm-icon-badge{
  width: 56px;
  height: 56px;
  border-radius: 9999px;
  display: grid;
  place-items: center;
  margin: 0 auto 10px;
  background: #fee2e2;
  color: #b91c1c;
  border: 1px solid #fecaca;
  box-shadow: 0 10px 26px rgba(185,28,28,.15);
  font-size: 22px;
}

.cxm-title-lg{
  font-size: 1.15rem;
  font-weight: 900;
  margin: 0;
}

.cxm-msg-lg{
  max-width: 420px;
}


  /* ================= ConfirmExitModal ================= */
  .cxm-overlay { position: fixed; inset: 0; z-index: 10001; display: grid; place-items: center; padding:16px; background: rgba(0,0,0,0.6); backdrop-filter: blur(4px); animation: cxmFade .2s ease; }
  .cxm-card {
    width: min(90vw, 360px); background: #ffffff; color: var(--rt-text-main); border-radius: 20px; border: 1px solid var(--rt-card-border);
    box-shadow: 0 30px 80px rgba(0,0,0,.4), 0 4px 12px rgba(0,0,0,.15); padding:20px 16px 16px; text-align:center; position:relative; animation: cxmPop .28s cubic-bezier(0.16,1,0.3,1);
  }
  .cxm-ribbon {
    position:absolute; top:0; left:0; right:0; background:linear-gradient(90deg,#dc2626,#ef4444);
    border-radius:20px 20px 0 0; padding:6px 12px; display:flex; align-items:center; justify-content:center; gap:6px; color:#fff;
    font-size:.7rem; font-weight:700; letter-spacing:.05em; text-transform:uppercase; box-shadow:0 8px 20px rgba(220,38,38,.4);
  }
  .cxm-title { margin:0; font-size:1rem; font-weight:700; color:var(--rt-text-main); line-height:1.3; }
  .cxm-msg { margin:8px auto 16px; font-size:.9rem; line-height:1.4; color:var(--rt-text-dim); max-width:260px; white-space: pre-line; }
  .cxm-actions { display:flex; flex-wrap:wrap; justify-content:center; gap:10px; }
  .cxm-btn-cancel { min-width:110px; justify-content:center; font-size:.85rem; }
  .cxm-btn-confirm {
    min-width:110px; justify-content:center; font-size:.85rem; background:linear-gradient(90deg,#dc2626,#ef4444);
    color:#fff; border:none; box-shadow:0 10px 24px rgba(239,68,68,.3);
  }
  .cxm-btn-confirm:hover{ filter:brightness(1.05); }
  @keyframes cxmFade { from { opacity:0; } to { opacity:1; } }
  @keyframes cxmPop { 0%{ opacity:0; transform:scale(.8) translateY(10px); } 60%{ opacity:1; transform:scale(1.03) translateY(0); } 100%{ transform:scale(1); } }

     /* ====== Buy Comodín Modal (claro, morado + verde) ====== */
  .buy-overlay{
    position:fixed;
    inset:0;
    z-index:9998;
    display:grid;
    place-items:center;
    padding:16px;
    background:rgba(15,23,42,.45);
    backdrop-filter:blur(6px);
    animation: cxmFade .2s ease;
  }

  .buy-card{
    width:min(92vw, 420px);
    background:#ffffff;
    border-radius:20px;
    border:1px solid #e5e7eb;
    box-shadow:0 24px 60px rgba(15,23,42,.28);
    position:relative;
    padding:42px 18px 16px;
    color:#0f172a;
  }

  .buy-ribbon{
    position:absolute;
    top:0;
    left:0;
    right:0;
    height:32px;
    display:flex;
    align-items:center;
    justify-content:center;
    background:linear-gradient(90deg,#4f46e5,#7c3aed);
    box-shadow:0 10px 20px rgba(79,70,229,.35);
    border-radius:20px 20px 0 0;
  }

  .buy-ribbon-pill{
    padding:4px 10px;
    border-radius:9999px;
    border:1px solid rgba(255,255,255,.5);
    color:#f9fafb;
    font-size:.72rem;
    font-weight:800;
    letter-spacing:.08em;
    text-transform:uppercase;
    backdrop-filter:blur(3px);
    background:linear-gradient(120deg,rgba(255,255,255,.28),rgba(255,255,255,.08));
  }

  .buy-close{
    position:absolute;
    top:6px;
    right:8px;
    width:26px;
    height:26px;
    border-radius:9999px;
    border:none;
    background:rgba(15,23,42,.08);
    color:#0f172a;
    font-weight:900;
    font-size:1rem;
    cursor:pointer;
    display:flex;
    align-items:center;
    justify-content:center;
  }

  .buy-header{
    display:flex;
    align-items:flex-start;
    gap:10px;
    margin-bottom:12px;
  }

  .buy-icon-wrap{
    flex:0 0 auto;
    width:52px;
    height:52px;
    border-radius:9999px;
    background:radial-gradient(120px 90px at 30% 0%,rgba(129,140,248,.35),transparent 65%),#f5f3ff;
    border:1px solid #ddd6fe;
    display:flex;
    align-items:center;
    justify-content:center;
  }

  .buy-icon-circle{
    width:32px;
    height:32px;
    border-radius:9999px;
    display:flex;
    align-items:center;
    justify-content:center;
    background:linear-gradient(135deg,#4f46e5,#7c3aed);
    color:#f9fafb;
    font-weight:800;
    font-size:1rem;
    box-shadow:0 8px 18px rgba(79,70,229,.4);
  }

  .buy-header-text{
    flex:1 1 auto;
    min-width:0;
  }

  .buy-title{
    margin:0;
    font-size:1.1rem;
    font-weight:900;
    color:#111827;
  }

  .buy-sub{
    margin:4px 0 0;
    font-size:.9rem;
    color:#4b5563;
    line-height:1.45;
  }

  .buy-stats{
    margin-top:12px;
    display:grid;
    grid-template-columns:repeat(3,minmax(0,1fr));
    gap:8px;
  }

  .buy-stat{
    padding:8px 10px;
    border-radius:12px;
    border:1px solid #e5e7eb;
    background:#f9fafb;
  }

  .buy-stat-main{
    background:linear-gradient(135deg,#f5f3ff,#eef2ff);
    border-color:#ddd6fe;
  }

  .buy-stat-label{
    font-size:.72rem;
    text-transform:uppercase;
    letter-spacing:.06em;
    color:#6b7280;
    margin-bottom:2px;
  }

  .buy-stat-value{
    font-size:.98rem;
    font-weight:800;
    color:#111827;
  }

  .buy-warning{
    margin-top:10px;
    padding:8px 10px;
    border-radius:12px;
    background:#fef2f2;
    border:1px solid #fecaca;
    color:#b91c1c;
    font-size:.85rem;
  }

  .buy-note{
    margin-top:8px;
    padding:8px 10px;
    border-radius:12px;
    background:#f0fdf4;
    border:1px solid #bbf7d0;
    color:#166534;
    font-size:.8rem;
  }

  .buy-actions{
    margin-top:14px;
    display:flex;
    justify-content:flex-end;
    gap:8px;
    flex-wrap:wrap;
  }

  @media(max-width:480px){
    .buy-card{
      padding-inline:14px;
    }
    .buy-stats{
      grid-template-columns:1fr;
    }
  }


  /* ====== Responsivo ====== */
  @media (min-width: 768px){
    .rt-header-mobile { display:none; }
  }

  @media (max-width: 767px){
    .rt-container {
      grid-template-columns: 1fr;
      grid-template-areas:
        "sidebar"
        "main";
    }
    .rt-sidebar { position: static; }
    .rt-topbar { display:none; }
  }

  @media (min-width: 640px){
    .rt-review-grid { grid-template-columns: 1fr 1fr; }
    .rt-explain { grid-column: 1 / -1; }
  }

  @media (max-width:480px){
    .rt-done-dark{ padding:16px; border-radius:14px; }
    .rt-done-title-dark{ font-size:1.1rem; }
    .rt-done-desc-dark{ font-size:.8rem; }
    .rt-buttons{ flex-direction:column; }
    .rt-buttons .rt-btn{ width:100%; justify-content:center; }
  }

  @keyframes rtGrow { from { width:0 } to { width: var(--rt-pct,100%) } }
  @keyframes rtMove { 0%{ background-position:0 0 } 100%{ background-position:40px 0 } }

  .q-comodines-cta{
  animation: comodinPulse 1s ease-in-out infinite;
  box-shadow: 0 0 0 0 rgba(79,70,229,.45);
}
@keyframes comodinPulse{
  0%{ transform: translateY(0); box-shadow: 0 0 0 0 rgba(79,70,229,.45); }
  70%{ transform: translateY(-1px); box-shadow: 0 0 0 10px rgba(79,70,229,0); }
  100%{ transform: translateY(0); box-shadow: 0 0 0 0 rgba(79,70,229,0); }
}

  /* ====== Tarjeta de Comodín (sidebar) ====== */
  .rt-comodin-card{
    position:relative;
    overflow:hidden;
    border-color: var(--rt-purple-border);
    background: linear-gradient(135deg,#ffffff 0%,#f5f3ff 100%);
  }
  .rt-comodin-card::before{
    content:"";
    position:absolute;
    inset:-40%;
    background:
      radial-gradient(260px 160px at -10% 0%, rgba(129,140,248,.18), transparent 60%),
      radial-gradient(220px 140px at 110% 100%, rgba(94,234,212,.18), transparent 65%);
    opacity:.9;
    pointer-events:none;
  }
  .rt-comodin-header{
    position:relative;
    z-index:1;
  }
  .rt-comodin-body{
    position:relative;
    z-index:1;
    margin-top:10px;
    display:flex;
    flex-direction:column;
    gap:8px;
  }

  .rt-comodin-icon{
    width:32px;
    height:32px;
    border-radius:9999px;
    display:flex;
    align-items:center;
    justify-content:center;
    background:var(--rt-purple-bg);
    border:1px solid var(--rt-purple-border);
    color:var(--rt-purple-main);
    box-shadow:0 4px 10px rgba(109,40,217,.18);
  }

  .rt-comodin-title{
    font-size:.9rem;
    font-weight:900;
    color:var(--rt-text-main);
  }
  .rt-comodin-sub{
    font-size:.78rem;
    color:var(--rt-text-dim);
    font-weight:600;
  }

  .rt-comodin-status{
    display:flex;
    align-items:flex-start;
  }

  .rt-tag{
    display:inline-flex;
    align-items:center;
    gap:6px;
    padding:4px 9px;
    border-radius:9999px;
    font-size:.78rem;
    font-weight:700;
    border:1px solid transparent;
    white-space:nowrap;
  }
  .rt-tag-price{
    background:linear-gradient(135deg,#f5f3ff,#eef2ff);
    border-color:#ddd6fe;
    color:#4c1d95;
  }
  .rt-tag-price svg{
    width:14px;
    height:14px;
  }
  .rt-tag-muted{
    background:#f9fafb;
    border-color:#e5e7eb;
    color:#9ca3af;
  }

  .rt-comodin-balance{
    gap:6px;
  }

  .rt-inline-label{
    display:inline-flex;
    align-items:center;
    gap:4px;
    padding:4px 8px;
    border-radius:9999px;
    background:#f9fafb;
    border:1px solid #e5e7eb;
    font-size:.8rem;
  }
  .rt-inline-label-soft{
    background:#eef2ff;
    border-color:#c7d2fe;
  }
  .rt-inline-label-name{
    color:#6b7280;
    font-weight:600;
    text-transform:uppercase;
    letter-spacing:.04em;
    font-size:.7rem;
  }
  .rt-inline-label-value{
    color:#111827;
    font-weight:800;
  }

  .rt-comodin-btn{
    width:100%;
    justify-content:center;
    margin-top:4px;
  }

  /* ✅ Botón desaturado cuando no hay suficientes monedas */
.rt-btn-disabled-bw {
  filter: grayscale(100%);
  opacity: 0.6;
  cursor: not-allowed;
}

.rt-btn-disabled-bw:hover {
  transform: none !important;
  filter: grayscale(100%) !important;
}

  .rt-comodin-hint{
    font-size:.8rem;
    color:#b91c1c;
    background:#fef2f2;
    border-radius:10px;
    border:1px solid #fee2e2;
    padding:6px 8px;
    font-weight:600;
  }

  /* Banners de resultado compra */
  .rt-banner{
    margin-top:6px;
    border-radius:10px;
    padding:6px 8px;
    font-size:.8rem;
    font-weight:600;
  }
  .rt-banner-ok{
    background:var(--rt-green-bg);
    border:1px solid var(--rt-green-border);
    color:var(--rt-green-main);
  }
  .rt-banner-err{
    background:#fef2f2;
    border:1px solid #fecaca;
    color:#b91c1c;
  }

/* ================================
    TUTORIAL OVERLAY (SIMPLIFICADO)
   ================================ */
.tour-root {
  position: fixed;
  inset: 0;
  z-index: 99999;
  display: grid;
  place-items: center;
  padding: 16px;
  pointer-events: auto;
}

/* Fondo transparente con blur muy suave */
.tour-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.10);
  backdrop-filter: blur(6px) saturate(1.08);
  -webkit-backdrop-filter: blur(6px) saturate(1.08);
  animation: tourFadeIn 0.3s ease-out both;
}

@keyframes tourFadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

/* Tarjeta del tutorial (siempre centrada) */
.tour-card {
  position: relative;
  width: min(540px, calc(100vw - 32px));
  background: #ffffff;
  border: 1px solid rgba(226, 232, 240, 0.98);
  border-radius: 20px;
  box-shadow: 0 40px 140px rgba(0, 0, 0, 0.6);
  z-index: 100002;
  padding: 18px 16px 14px;
  animation: tourPopIn 0.35s cubic-bezier(0.16, 1, 0.3, 1) both;
  pointer-events: auto;
  cursor: default;
}

.tour-card-centered {
  margin: 0 auto;
}

@keyframes tourPopIn {
  from {
    transform: scale(0.92) translateY(20px);
    opacity: 0;
  }
  to {
    transform: scale(1) translateY(0);
    opacity: 1;
  }
}

.tour-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 8px;
}

.tour-title {
  color: #0f172a;
  font-weight: 950;
  letter-spacing: 0.3px;
  font-size: 1.1rem;
  line-height: 1.3;
}

.tour-step {
  font-weight: 900;
  font-size: 0.78rem;
  padding: 6px 11px;
  border-radius: 999px;
  background: linear-gradient(135deg, #eef2ff, #e0e7ff);
  border: 1px solid #c7d2fe;
  color: #3730a3;
  flex: 0 0 auto;
  box-shadow: 0 2px 8px rgba(99, 102, 241, 0.15);
}

.tour-text {
  margin-top: 10px;
  color: #334155;
  line-height: 1.65;
  font-size: 0.96rem;
  max-height: 60vh;
  overflow-y: auto;
  padding-right: 4px;
}

.tour-p {
  margin: 0 0 12px 0;
  white-space: pre-line;
}

.tour-p:last-child {
  margin-bottom: 0;
}

/* Formato especial para negritas en el texto del tutorial */
.tour-text strong,
.tour-p strong {
  color: #0f172a;
  font-weight: 800;
}

/* Viñetas con mejor espaciado */
.tour-text li,
.tour-p li {
  margin-bottom: 8px;
  line-height: 1.6;
}

/* Títulos dentro del texto */
.tour-text h4,
.tour-p h4 {
  margin: 16px 0 8px 0;
  font-size: 1rem;
  font-weight: 900;
  color: #1e293b;
  display: flex;
  align-items: center;
  gap: 6px;
}

.tour-text h4:first-child,
.tour-p h4:first-child {
  margin-top: 0;
}

.tour-actions {
  margin-top: 16px;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
  padding-top: 12px;
  border-top: 1px solid #f1f5f9;
}

.tour-btn {
  border: none;
  border-radius: 12px;
  padding: 11px 16px;
  font-weight: 950;
  cursor: pointer;
  font-size: 0.9rem;
  transition: transform 0.12s ease, filter 0.12s ease, background 0.12s ease, box-shadow 0.15s ease;
  pointer-events: auto;
}

.tour-btn.primary {
  background: linear-gradient(135deg, #2563eb, #1d4ed8);
  color: #fff;
  border: 1px solid #1d4ed8;
  box-shadow: 0 14px 34px rgba(37, 99, 235, 0.25), inset 0 1px 0 rgba(255, 255, 255, 0.35);
}

.tour-btn.primary:hover {
  transform: translateY(-2px);
  filter: brightness(1.08);
  box-shadow: 0 18px 40px rgba(37, 99, 235, 0.32);
}

.tour-btn.ghost {
  background: #fff;
  border: 1px solid #e5e7eb;
  color: #0f172a;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.04);
}

.tour-btn.ghost:hover {
  background: #f8fafc;
  transform: translateY(-1px);
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.08);
}

/* Responsive */
@media (max-width: 640px) {
  .tour-card {
    width: calc(100vw - 24px);
    padding: 16px 14px 12px;
  }

  .tour-title {
    font-size: 1rem;
  }

  .tour-text {
    font-size: 0.92rem;
  }

  .tour-actions {
    flex-direction: column;
    align-items: stretch;
  }

  .tour-btn {
    width: 100%;
    justify-content: center;
  }
}

/* ✅ Responsive mejorado */
@media (max-width: 767px) {
  .tour-card {
    width: calc(100vw - 32px);
    left: 16px !important;
    right: 16px !important;
    bottom: 20px !important;
    top: auto !important;
    transform: none !important;
  }

  .tour-card.centered {
    bottom: 50% !important;
    transform: translateY(50%) !important;
  }

  .tour-actions {
    flex-direction: column;
    align-items: stretch;
  }

  .tour-btn {
    flex: 1;
  }
}

@media (min-width: 768px) and (max-width: 1024px) {
  .tour-card {
    width: min(480px, calc(100vw - 48px));
  }
}


  `}</style>
);
